/* eslint-disable @typescript-eslint/no-explicit-any */
import { Textbox, Canvas } from 'fabric';

import {
  TextEntityType,
  TextEntityOptions,
  isBulletType,
  getBulletLevel,
  getBulletTypeForLevel,
  MetaBulletStylesConfig,
  MarkerLayoutConfig,
  MarkerType,
  isDigitalAccentType,
  isDigitalAccentDigit,
  isDigitalAccentSuperscript,
  TextEntityStyleConfig,
} from '../../types/fabric-extensions';

import { useCanvasStore } from '../../store';

import { mergeStylesConfig } from '../../config/textEntityStyles';
import { generateObjectId } from '../../utils/generateId';
import { scaleImage } from '../../utils/imageUtils';

import { Marker } from './Marker';

/**
 * Кастомный класс TextEntity наследующийся от Textbox
 * Основа системы метабуллитов с встроенной логикой
 */
export class TextEntity extends Textbox {
  // Кастомные свойства метабуллитов
  id: string;
  entityType: TextEntityType;
  metaBulletId: string;
  entityOrder: number;
  bulletLevel?: number;
  isMetaBulletEntity: boolean;
  marginBottom?: number;
  marginLeft?: number;
  excludeFromBackup?: boolean;

  // Маркер (если есть)
  markerId?: string;
  private marker?: Marker;

  // Персональные настройки маркера (переопределяют настройки из стилей)
  markerContent?: string; // Персональное содержимое маркера
  markerType?: MarkerType; // Персональный тип маркера

  // 🔄 Сохраненное состояние маркера для восстановления при конвертации типов
  private lastMarkerState?: {
    markerId: string;
    marker: Marker;
    markerContent?: string;
    markerType?: MarkerType;
  };

  // === ПОЛЯ ДЛЯ DIGITAL-ACCENT ===
  accentGroupId?: string; // ID группы для связывания digit и степени
  accentRole?: 'digit' | 'superscript' | 'subscript'; // Роль в группе
  linkedAccentEntityId?: string; // ID связанной сущности
  private linkedAccentEntity?: TextEntity; // Кэш связанной сущности

  // Персональные стили для digital-accent
  personalDigitStyles?: Partial<
    TextEntityStyleConfig & { spacingTop?: number; spacingBottom?: number }
  >;
  personalSuperscriptStyles?: Partial<
    TextEntityStyleConfig & { offsetX?: number; offsetY?: number }
  >;

  // Конфигурация стилей (хранится для использования в методах)
  private stylesConfig: MetaBulletStylesConfig;

  // Статический тип для регистрации в Fabric.js
  static type = 'textEntity';

  constructor(text: string, options: TextEntityOptions = {}) {
    // Получаем объединенную конфигурацию стилей (дефолтные + пользовательские)
    const stylesConfig = mergeStylesConfig(options.stylesConfig);

    // Получаем дефолтные стили для типа сущности
    const entityType = options.entityType || 'bullet-level-1';
    const defaultStyles = TextEntity.getDefaultStylesForType(entityType, stylesConfig);

    // Объединяем дефолтные стили с переданными опциями
    const mergedOptions = {
      ...defaultStyles,
      ...options,
      // Гарантируем обязательные значения чтобы избежать undefined
      fontFamily: options.fontFamily || defaultStyles.fontFamily || 'Arial',
      fontSize: options.fontSize || defaultStyles.fontSize || 16,
      fill: options.fill || defaultStyles.fill || '#000000',
      // Гарантируем что это метабуллит объект
      isMetaBulletEntity: true,
    };

    // Фильтруем опции для родительского конструктора - убираем кастомные поля
    const fabricOptions = {
      left: mergedOptions.left,
      top: mergedOptions.top,
      width: mergedOptions.width,
      height: mergedOptions.height,
      fontSize: mergedOptions.fontSize,
      fontFamily: mergedOptions.fontFamily,
      fontWeight: mergedOptions.fontWeight,
      fill: mergedOptions.fill,
      textAlign: mergedOptions.textAlign,
      lineHeight: mergedOptions.lineHeight,
      originX: mergedOptions.originX, // Сохраняем originX из бэкенда
      originY: mergedOptions.originY, // Сохраняем originY из бэкенда
    };

    // Убираем undefined значения
    const cleanedOptions = Object.fromEntries(
      Object.entries(fabricOptions).filter(([, value]) => value !== undefined),
    );

    // Вызываем родительский конструктор только со стандартными опциями
    super(text, cleanedOptions as any);

    // Устанавливаем конфигурацию стилей после вызова super()
    this.stylesConfig = stylesConfig;

    // Устанавливаем кастомные свойства
    this.id = options.id || '';
    this.entityType = entityType;
    this.metaBulletId = options.metaBulletId || '';
    this.entityOrder = options.entityOrder || 0;
    this.isMetaBulletEntity = true;
    this.excludeFromBackup = options.excludeFromBackup || false;

    // === DIGITAL-ACCENT СВОЙСТВА ===
    this.accentGroupId = options.accentGroupId;
    this.accentRole = options.accentRole;
    this.linkedAccentEntityId = options.linkedAccentEntityId;
    this.personalDigitStyles = options.personalDigitStyles;
    this.personalSuperscriptStyles = options.personalSuperscriptStyles;

    // Если это буллит, устанавливаем уровень и отступы
    if (isBulletType(entityType)) {
      this.bulletLevel = options.bulletLevel || getBulletLevel(entityType);
      // НОВАЯ СИСТЕМА v7.0: marginLeft из опций или из стилей или 0 (без автоматического расчета)
      // Автоматический расчет вложенности теперь делает getAutomaticParentMarkerOffset()
      this.marginLeft = options.marginLeft ?? this.getStylesForType(entityType).marginLeft ?? 0;
    }

    // Устанавливаем нижний отступ
    this.marginBottom = options.marginBottom || defaultStyles.marginBottom;

    // === ИНИЦИАЛИЗАЦИЯ ПЕРСОНАЛЬНЫХ НАСТРОЕК МАРКЕРА ===
    this.markerContent = options.markerContent;
    this.markerType = options.markerType;

    // === ИНИЦИАЛИЗАЦИЯ МАРКЕРА ===
    // Маркеры теперь всегда передаются с бэкенда как отдельные объекты
    // Связывание происходит в loadObjectsWithLayers через linkMarkersWithContentObjects

    // === ИНИЦИАЛИЗАЦИЯ ПОЛЕЙ ДЛЯ ОБРАБОТКИ ENTER ===
    // Время последнего создания дубликата сущности (защита от спама)
    this._lastDuplicateTime = 0;
    // Ссылка на обработчик клавиш (будет установлена при входе в режим редактирования)
    this._enterKeyHandler = null;

    // Подписываемся на событие изменения текста через canvas
    // Это нужно делать после добавления объекта на canvas
    this.on('added', () => {
      if (this.canvas) {
        // Проверяем, есть ли уже маркер для этой сущности на канвасе
        const existingMarker = this.findExistingMarkerOnCanvas();

        if (existingMarker) {
          // Маркер уже есть (загружен из JSON) - связываем его с сущностью

          this.marker = existingMarker;
          this.markerId = existingMarker.id;

          // ВАЖНО: Сразу обновляем позицию персонального маркера относительно текста
          this.updateMarkerPosition();
        } else {
          // Маркера нет - это нормально, теперь маркеры всегда передаются с бэкенда
        }

        this.canvas.on('text:changed', (e) => {
          if (e.target === this) {
            this._onTextChanged();
          }
        });

        // === ОБРАБОТЧИК ВХОДА В РЕЖИМ РЕДАКТИРОВАНИЯ ===
        // При входе в режим редактирования добавляем глобальный обработчик клавиш
        // для отслеживания нажатия Enter и создания дубликатов сущностей
        this.on('editing:entered' as any, () => {
          // Сохраняем исходный текст для сравнения при выходе из редактирования
          (this as any)._originalTextBeforeEditing = this.text || '';

          // === ЗАЩИТА ОТ ДУБЛИРОВАНИЯ ОБРАБОТЧИКОВ ===
          // Проверяем, что обработчик еще не добавлен (предотвращаем множественные event listeners)
          if (this._enterKeyHandler) {
            return;
          }

          // Сохраняем ссылки на методы для вызова из обработчика
          const deleteMethod = this._deleteEmptyEntityAndFocusPrevious.bind(this);
          const splitMethod = this._splitTextIntoTwoEntities.bind(this);
          const createMethod = this._createEntityDuplicate.bind(this);

          // 🔥 КЛЮЧЕВОЕ РЕШЕНИЕ: Вешаем обработчик на hiddenTextarea, а НЕ на document
          // Это позволяет:
          // 1. Сохранить позицию курсора ДО обработки Fabric.js (через capture: true)
          // 2. Для Shift+Enter не блокировать событие - Fabric.js обработает его
          const hiddenTextarea = (this as any).hiddenTextarea;
          let savedCursorPosition = 0;

          // Pre-обработчик для сохранения позиции курсора ДО Fabric.js
          const captureHandler = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              savedCursorPosition = hiddenTextarea?.selectionStart ?? 0;
              console.warn('💾 [CAPTURE] Saved cursor position:', {
                savedCursorPosition,
                shiftKey: e.shiftKey,
              });
            }

            // === ОБРАБОТКА СТРЕЛОК В CAPTURE-ФАЗЕ (ДО Fabric.js) ===
            if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
              const cursorPosition = hiddenTextarea?.selectionStart ?? this.selectionStart ?? 0;
              const selectionStart = hiddenTextarea?.selectionStart ?? this.selectionStart ?? 0;
              const selectionEnd = hiddenTextarea?.selectionEnd ?? this.selectionEnd ?? 0;
              const hasSelection = selectionStart !== selectionEnd;

              // Если есть выделенный текст - позволяем стандартное поведение
              if (hasSelection) {
                console.debug('⬆️ Has selection, skipping navigation');
                return;
              }

              // Если курсор в начале текста - переходим к предыдущей сущности с курсором в конце
              if (cursorPosition === 0) {
                e.preventDefault();
                e.stopPropagation();
                this._navigateToPreviousEntity();
              } else {
                console.debug('⬆️ Cursor not at start, allowing default behavior');
              }
            }

            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
              const cursorPosition = hiddenTextarea?.selectionStart ?? this.selectionStart ?? 0;
              const textLength = (this.text || '').length;
              const selectionStart = hiddenTextarea?.selectionStart ?? this.selectionStart ?? 0;
              const selectionEnd = hiddenTextarea?.selectionEnd ?? this.selectionEnd ?? 0;
              const hasSelection = selectionStart !== selectionEnd;

              // Если есть выделенный текст - позволяем стандартное поведение
              if (hasSelection) {
                console.debug('⬇️ Has selection, skipping navigation');
                return;
              }

              // Если курсор в конце текста - переходим к следующей сущности с курсором в начале
              if (cursorPosition >= textLength) {
                e.preventDefault();
                e.stopPropagation();
                this._navigateToNextEntity();
              } else {
                console.debug('⬇️ Cursor not at end, allowing default behavior');
              }
            }
          };

          // Основной обработчик (срабатывает ПОСЛЕ Fabric.js)
          const keydownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              // Для Shift+Enter - вручную добавляем перенос строки
              if (e.shiftKey) {
                e.preventDefault(); // Блокируем стандартное поведение

                // Получаем текущий текст и позицию курсора
                const currentText = this.text || '';
                const cursorPos = savedCursorPosition;

                // Вставляем \n в позицию курсора
                const textBefore = currentText.substring(0, cursorPos);
                const textAfter = currentText.substring(cursorPos);
                const newText = textBefore + '\n' + textAfter;

                console.warn('✅ SHIFT+ENTER - Вручную добавляем перенос строки', {
                  cursorPos,
                  textBefore: `"${textBefore}"`,
                  textAfter: `"${textAfter}"`,
                  newText: `"${newText}"`,
                });

                // Устанавливаем новый текст
                this.set('text', newText);

                // 🔥 КРИТИЧЕСКИ ВАЖНО: Обновляем hiddenTextarea.value вручную
                // Иначе при следующем вводе Fabric.js возьмет старое значение без \n
                if (hiddenTextarea) {
                  hiddenTextarea.value = newText;
                }

                // Перемещаем курсор после \n
                const newCursorPos = cursorPos + 1;
                this.selectionStart = newCursorPos;
                this.selectionEnd = newCursorPos;

                // 🔥 КРИТИЧЕСКИ ВАЖНО: Синхронизируем позицию курсора в hiddenTextarea
                if (hiddenTextarea) {
                  hiddenTextarea.selectionStart = newCursorPos;
                  hiddenTextarea.selectionEnd = newCursorPos;
                }

                // Обновляем canvas
                if (this.canvas) {
                  this.canvas.requestRenderAll();
                }

                // Пересчитываем позиции MetaBullet для обновления отступов
                if (this.canvas && this.metaBulletId) {
                  const system = (this.canvas as any)._metaBulletSystem;
                  if (system) {
                    const metaBullet = system.getMetaBullet(this.metaBulletId);
                    if (metaBullet) {
                      // Используем setTimeout для пересчета после рендера
                      setTimeout(() => {
                        metaBullet.recalculatePositions(true);
                      }, 50);
                    }
                  }
                }

                return;
              }

              // === СПЕЦИАЛЬНАЯ ЛОГИКА ДЛЯ DIGITAL-ACCENT ===
              if (this.isDigitalAccent()) {
                // Для digital-accent НЕ создаем дубликаты, только новый параграф ниже
                e.preventDefault();
                // Создаем новый параграф после всей группы digital-accent
                this._createParagraphAfterDigitalAccent();
                return;
              }

              // Проверяем позицию курсора
              const textLength = (this.text || '').length;
              const cursorPosition = savedCursorPosition;
              const isAtEndOfText = cursorPosition >= textLength;

              // Блокируем стандартное поведение Enter (добавление переноса строки)
              e.preventDefault();

              // === ЗАЩИТА ОТ СПАМА СОЗДАНИЯ ДУБЛИКАТОВ ===
              // Проверяем, чтобы не создавать дубликаты слишком часто
              // Это предотвращает случайное создание множественных сущностей
              // при быстром повторном нажатии Enter
              const currentTime = Date.now();
              const timeSinceLastDuplicate = currentTime - (this._lastDuplicateTime || 0);
              const minDuplicateInterval = 500; // Минимум 500ms между созданиями

              if (timeSinceLastDuplicate < minDuplicateInterval) {
                return;
              }

              // Проверяем, пустая ли сущность
              const isEmpty = !this.text || this.text.length === 0;

              if (isEmpty) {
                // === ПУСТАЯ СТРОКА: УДАЛЯЕМ И ПЕРЕКЛЮЧАЕМСЯ НА СЛЕДУЮЩУЮ ===
                this._deleteEmptyEntityAndFocusNext();
                return; // ⚠️ КРИТИЧЕСКИ ВАЖНО: не продолжаем выполнение!
              }

              // Проверяем, находится ли курсор в начале непустой сущности
              const isAtStart = cursorPosition === 0;
              if (!isEmpty && isAtStart) {
                // === КУРСОР В НАЧАЛЕ НЕ ПУСТОЙ СУЩНОСТИ: НЕ ДЕЛАЕМ НИЧЕГО ===
                return; // Просто игнорируем Enter
              }

              // Обновляем время последнего создания дубликата
              this._lastDuplicateTime = currentTime;

              // Разделяем текст или создаем пустой дубликат
              if (isAtEndOfText) {
                // === СОЗДАНИЕ ПУСТОГО ДУБЛИКАТА В КОНЦЕ ===

                createMethod();
              } else {
                // === РАЗДЕЛЕНИЕ ТЕКСТА НА ДВЕ СУЩНОСТИ ===

                splitMethod(cursorPosition);
              }

              // Выходим из режима редактирования текущей сущности
              this.exitEditing();
            }

            if (e.key === 'Backspace') {
              // === ОБРАБОТКА BACKSPACE ДЛЯ УДАЛЕНИЯ ПУСТЫХ СУЩНОСТЕЙ ===
              const hiddenTextarea = (this as any).hiddenTextarea;
              const backspaceCursorPosition =
                hiddenTextarea?.selectionStart ?? this.selectionStart ?? 0;
              const selectionStart = hiddenTextarea?.selectionStart ?? this.selectionStart ?? 0;
              const selectionEnd = hiddenTextarea?.selectionEnd ?? this.selectionEnd ?? 0;
              const hasSelection = selectionStart !== selectionEnd;

              //  Если есть выделенный текст - позволяем стандартное поведение (удаление выделенного текста)
              if (hasSelection) {
                return; // Позволяем стандартное поведение Backspace для удаления выделенного текста
              }

              // === СПЕЦИАЛЬНАЯ ЛОГИКА ДЛЯ DIGITAL-ACCENT ===
              if (this.isDigitalAccent()) {
                const isEmpty = !this.text || this.text.length === 0;
                const isAtStart = backspaceCursorPosition === 0;

                if (isEmpty && isAtStart) {
                  e.preventDefault();
                  this._handleDigitalAccentBackspace();
                  return;
                } else if (!isEmpty && isAtStart) {
                  e.preventDefault();
                  this._focusPreviousEntityDigitalAccent();
                  return;
                }
                // Если курсор не в начале - позволяем стандартное поведение
                return;
              }

              // Проверяем, что текст пустой и курсор в начале
              const isEmpty = !this.text || this.text.length === 0;
              const isAtStart = backspaceCursorPosition === 0;

              if (isEmpty && isAtStart) {
                // Блокируем стандартное поведение Backspace
                e.preventDefault();

                deleteMethod();
              } else if (!isEmpty && isAtStart) {
                // Блокируем стандартное поведение Backspace
                e.preventDefault();

                // Курсор в начале НЕ пустой сущности - переключаемся на предыдущую (БЕЗ удаления)
                this._focusPreviousEntityOnly();
              }
            }
          };

          // === РЕГИСТРАЦИЯ ОБРАБОТЧИКОВ НА HIDDENTEXTAREA ===
          // Регистрируем оба обработчика на hiddenTextarea
          if (hiddenTextarea) {
            // Capture-обработчик для сохранения позиции ДО Fabric.js
            hiddenTextarea.addEventListener('keydown', captureHandler, { capture: true });
            // Обычный обработчик ПОСЛЕ Fabric.js
            hiddenTextarea.addEventListener('keydown', keydownHandler);
          }

          // Сохраняем ссылки на обработчики для предотвращения дублирования
          this._enterKeyHandler = keydownHandler;
          (this as any)._captureHandler = captureHandler;

          // === ОЧИСТКА ОБРАБОТЧИКА ПРИ ВЫХОДЕ ИЗ РЕДАКТИРОВАНИЯ ===
          // Функция для корректного удаления обработчика и очистки ссылок
          const removeHandler = () => {
            // Удаляем оба обработчика с hiddenTextarea
            if (hiddenTextarea) {
              hiddenTextarea.removeEventListener('keydown', captureHandler, { capture: true });
              hiddenTextarea.removeEventListener('keydown', keydownHandler);
            }

            // Очищаем ссылки на обработчики
            this._enterKeyHandler = null;
            (this as any)._captureHandler = null;
            this.off('editing:exited' as any, removeHandler);

            // === СОЗДАНИЕ БЕКАПА ПРИ ВЫХОДЕ ИЗ РЕДАКТИРОВАНИЯ (если текст был изменен) ===
            // Проверяем, был ли изменен текст (для новых сущностей созданных через Enter или редактирования существующих)
            setTimeout(() => {
              const originalText = (this as any)._originalTextBeforeEditing || '';
              const currentText = this.text || '';
              const textChanged = originalText !== currentText;
              const hasText = currentText.trim().length > 0;

              //  Блокируем бекап для старой сущности, если мы только что создали новую через Enter
              // Это предотвращает создание бекапа при выходе из редактирования старой сущности
              // когда мы переходим в редактирование новой
              const isCreatingNewEntity = (this as any)._isCreatingNewEntity === true;
              if (isCreatingNewEntity) {
                // Сбрасываем флаг после проверки
                (this as any)._isCreatingNewEntity = false;
                return;
              }

              // Создаем бекап в двух случаях:
              // 1. Для новых сущностей (созданных через Enter) - только если текст был заполнен
              // 2. Для старых сущностей - если текст изменился
              const isNewlyCreated = (this as any)._isNewlyCreated === true;
              const shouldCreateBackup =
                (isNewlyCreated && textChanged && hasText) || // Новая сущность с заполненным текстом
                (!isNewlyCreated && textChanged); // Старая сущность с измененным текстом

              if (shouldCreateBackup && this.canvas) {
                const system = (this.canvas as any)._metaBulletSystem;
                const backupManager = system?.getBackupManager();
                if (backupManager && !backupManager.isOperationActive()) {
                  const operationType = isNewlyCreated ? 'entity_create' : 'text_changed';

                  backupManager.startOperation(operationType, [this.id]);
                  backupManager.requestBackup();
                  backupManager.finishOperation();
                }
              }

              // Очищаем сохраненный текст и флаг новой сущности
              (this as any)._originalTextBeforeEditing = undefined;
              (this as any)._isNewlyCreated = false; // Сбрасываем флаг после первого редактирования
              // ✅ Восстанавливаем возможность создания бекапов
              this.excludeFromBackup = false;

              // === ПРОВЕРКА НА ПУСТУЮ СУЩНОСТЬ ПОСЛЕ ВЫХОДА ИЗ РЕДАКТИРОВАНИЯ ===
              // Проверяем, остался ли текст после редактирования
              // Используем сохраненную ссылку на метод
              const checkMethod = this._checkAndRemoveIfEmpty.bind(this);
              checkMethod();
            }, 50); // Небольшая задержка для обновления текста
          };

          // Удаляем обработчик при выходе из редактирования
          this.on('editing:exited' as any, removeHandler);
        });
      }
    });

    // Подписываемся на событие удаления с канваса для очистки маркера
    this.on('removed', () => {
      // ВАЖНО: Проверяем, что объект действительно удален с canvas
      // а не просто перемещен в ActiveSelection или временно удален
      if (this.canvas) {
        const canvasObjects = this.canvas.getObjects();
        const stillOnCanvas = canvasObjects.some((obj: any) => obj.id === this.id);

        if (stillOnCanvas) {
          return;
        }
      }

      // Очищаем маркер при удалении сущности с канваса
      if (this.marker && this.canvas) {
        this.canvas.remove(this.marker);
        this.marker = undefined;
        this.markerId = undefined;
      }
    });

    // Подписываемся на событие moving для отслеживания драга в реальном времени
    this.on('moving', () => {
      // Обновляем позицию маркера во время драга
      this.updateMarkerPosition();
    });

    // Также подписываемся на событие modified для более надежного отслеживания изменений
    this.on('modified', () => {
      // Блокируем событие если мы в процессе split текста
      if ((this as any)._isSplitting) {
        return;
      }

      // Блокируем событие для новых сущностей (созданных через Enter, но еще не заполненных)
      // Бекап будет создан только при выходе из редактирования, если текст был изменен
      if ((this as any)._isNewlyCreated) {
        return;
      }

      // Проверяем состояние drag'а перед отправкой события
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const system = (this.canvas as any)?._metaBulletSystem;

      // Если мы находимся в процессе drag'а этой сущности, проверяем не вышли ли за границы
      if (system && system.currentDraggedEntity && system.currentDraggedEntity.id === this.id) {
        // Если вышли за границы, не отправляем уведомление
        if (system.isCurrentlyDragOutOfBounds) {
          return;
        }
      }

      // Обновляем позицию маркера при изменении позиции текста
      this.updateMarkerPosition();

      // Отправляем одно объединенное событие вместо двух отдельных
      this.notifyMetaBulletChanged('entity_modified', {
        newText: this.text,
        textLength: this.text?.length || 0,
        newLeft: this.left,
        newTop: this.top,
        newWidth: this.width,
        newHeight: this.height,
      });
    });

    // Также отслеживаем событие scaling для изменения размеров
    this.on('scaling', () => {
      // При масштабировании тоже может потребоваться пересчет
    });
  }

  /**
   * Получить стили для типа сущности из конфигурации
   */
  private getStylesForType(
    type: TextEntityType,
    config?: MetaBulletStylesConfig,
  ): Partial<TextEntityOptions> {
    // Используем переданную конфигурацию или сохраненную
    const styleConfig = config ? config[type] : this.stylesConfig[type];

    if (!styleConfig) {
      return {};
    }

    // Специальная обработка для digital-accent
    if (type === 'digital-accent' && 'digit' in styleConfig) {
      const digitConfig = styleConfig.digit;
      const baseStyles = {
        fontSize: digitConfig.fontSize,
        fontFamily: digitConfig.fontFamily,
        fontWeight: digitConfig.fontWeight,
        lineHeight: digitConfig.lineHeight,
        marginBottom: digitConfig.marginBottom,
        marginLeft: digitConfig.marginLeft,
        textAlign: digitConfig.textAlign as 'left' | 'center' | 'right' | 'justify',
        fill: digitConfig.fill,
      };

      // Применяем персональные стили для digit если они есть
      if (this.personalDigitStyles) {
        const merged = {
          ...baseStyles,
          ...this.personalDigitStyles,
        } as Partial<TextEntityOptions>;
        return merged;
      }

      return baseStyles;
    }

    // Для остальных типов (включая digital-accent-superscript)
    if ('fontSize' in styleConfig) {
      const baseStyles = {
        fontSize: styleConfig.fontSize,
        fontFamily: styleConfig.fontFamily,
        fontWeight: styleConfig.fontWeight,
        lineHeight: styleConfig.lineHeight,
        marginBottom: styleConfig.marginBottom,
        marginLeft: styleConfig.marginLeft,
        textAlign: styleConfig.textAlign as 'left' | 'center' | 'right' | 'justify',
        fill: styleConfig.fill,
      };

      // Применяем персональные стили для superscript если они есть
      if (type === 'digital-accent-superscript' && this.personalSuperscriptStyles) {
        const merged = {
          ...baseStyles,
          ...this.personalSuperscriptStyles,
        } as Partial<TextEntityOptions>;
        return merged;
      }

      return baseStyles;
    }

    return {};
  }

  /**
   * Получить дефолтные стили для типа сущности (статический метод для обратной совместимости)
   * @deprecated Используйте экземплярный метод getStylesForType с внешней конфигурацией
   */
  private static getDefaultStylesForType(
    type: TextEntityType,
    stylesConfig?: MetaBulletStylesConfig,
  ): Partial<TextEntityOptions> {
    // Используем переданную конфигурацию или создаем дефолтную
    const config = stylesConfig || mergeStylesConfig();
    const styleConfig = config[type];

    if (!styleConfig) {
      return {};
    }

    // Специальная обработка для digital-accent
    if (type === 'digital-accent' && 'digit' in styleConfig) {
      const digitConfig = styleConfig.digit;
      return {
        fontSize: digitConfig.fontSize,
        fontFamily: digitConfig.fontFamily,
        fontWeight: digitConfig.fontWeight,
        lineHeight: digitConfig.lineHeight,
        marginBottom: digitConfig.marginBottom,
        marginLeft: digitConfig.marginLeft,
        textAlign: digitConfig.textAlign as 'left' | 'center' | 'right' | 'justify',
        fill: digitConfig.fill,
      };
    }

    // Для остальных типов (включая digital-accent-superscript)
    if ('fontSize' in styleConfig) {
      return {
        fontSize: styleConfig.fontSize,
        fontFamily: styleConfig.fontFamily,
        fontWeight: styleConfig.fontWeight,
        lineHeight: styleConfig.lineHeight,
        marginBottom: styleConfig.marginBottom,
        marginLeft: styleConfig.marginLeft,
        textAlign: styleConfig.textAlign as 'left' | 'center' | 'right' | 'justify',
        fill: styleConfig.fill,
      };
    }

    return {};
  }

  /**
   * Вычислить отступ слева для уровня буллита
   * @deprecated Устаревший метод. В новой системе v7.0 marginLeft берется из стилей.
   * Автоматический расчет вложенности теперь делает getAutomaticParentMarkerOffset()
   */
  private calculateIndentForLevel(level: number): number {
    console.warn('⚠️ calculateIndentForLevel is deprecated! Use marginLeft from styles instead.');
    return level * 20; // 20px на каждый уровень
  }

  /**
   * Проверка является ли объект TextEntity
   */
  static isTextEntity(obj: unknown): obj is TextEntity {
    return (
      obj !== null &&
      typeof obj === 'object' &&
      'type' in obj &&
      (obj.type as string)?.toLowerCase() === 'textentity' &&
      'isMetaBulletEntity' in obj &&
      obj.isMetaBulletEntity === true
    );
  }

  /**
   * Получить информацию об объекте для отладки
   */
  getDebugInfo(): string {
    return `TextEntity[${this.entityType}] id: ${this.id}, metaBullet: ${this.metaBulletId}, order: ${this.entityOrder}`;
  }

  /**
   * Изменить тип сущности с автоприменением соответствующих стилей
   * @param newEntityType - Новый тип сущности
   */
  changeType(newEntityType: TextEntityType): void {
    // Сохраняем старый тип для уведомления
    const oldType = this.entityType;

    // Получаем новые стили для типа из конфигурации
    const newStyles = this.getStylesForType(newEntityType);

    // Сохраняем текущие позицию и размеры
    const currentLeft = this.left;
    const currentTop = this.top;
    const currentWidth = this.width;
    const currentHeight = this.height;

    // Обновляем тип сущности
    this.entityType = newEntityType;

    // Применяем новые стили
    this.set({
      fontSize: newStyles.fontSize,
      fontFamily: newStyles.fontFamily,
      fontWeight: newStyles.fontWeight,
      lineHeight: newStyles.lineHeight,
      // Сохраняем позицию и размеры
      left: currentLeft,
      top: currentTop,
      width: currentWidth,
      height: currentHeight,
    });

    // Если это буллит, устанавливаем уровень и отступы
    if (isBulletType(newEntityType)) {
      this.bulletLevel = getBulletLevel(newEntityType);
      // НОВАЯ СИСТЕМА v7.0: marginLeft берем из стилей
      this.marginLeft = newStyles.marginLeft || 0;
    } else {
      // Если это не буллит, убираем буллит-специфичные свойства
      this.bulletLevel = undefined;
      this.marginLeft = newStyles.marginLeft || 0;
    }

    // Обновляем отступы
    this.marginBottom = newStyles.marginBottom || 16;

    // Очищаем кэш и обновляем координаты
    this._clearCache();
    this.setCoords();

    // Обновляем маркер для нового типа сущности
    this.updateMarkerForNewType();

    // Уведомляем метабуллит об изменении типа
    this.notifyMetaBulletChanged('type_changed', {
      oldType: oldType,
      newType: newEntityType,
      fontSize: this.fontSize,
      bulletLevel: this.bulletLevel,
      marginLeft: this.marginLeft,
      marginBottom: this.marginBottom,
    });
  }

  /**
   * Изменить уровень буллита (только для bullet-level-* типов)
   * @param newLevel - Новый уровень буллита (0-4)
   */
  changeBulletLevel(newLevel: number): void {
    // Проверяем что это буллит
    if (!isBulletType(this.entityType)) {
      console.warn('⚠️ changeBulletLevel called on non-bullet entity:', this.entityType);
      return;
    }

    // Валидируем уровень в диапазоне 0-4
    const validLevel = Math.max(0, Math.min(4, newLevel));
    const newBulletType = getBulletTypeForLevel(validLevel) as TextEntityType;

    // Сохраняем текущие позицию и размеры
    const currentLeft = this.left;
    const currentTop = this.top;
    const currentWidth = this.width;
    const currentHeight = this.height;

    // Получаем стили для нового уровня буллита из конфигурации
    const newStyles = this.getStylesForType(newBulletType);

    // Обновляем тип и уровень
    this.entityType = newBulletType;
    this.bulletLevel = validLevel;

    // Пересчитываем отступ - берем из стилей (НОВАЯ СИСТЕМА v7.0)
    this.marginLeft = newStyles.marginLeft || 0;

    // Применяем новые стили (в основном отступы)
    this.set({
      // НЕ меняем fontSize, fontFamily и другие стили - только отступы
      left: currentLeft,
      top: currentTop,
      width: currentWidth,
      height: currentHeight,
    });

    // Обновляем отступ снизу
    this.marginBottom = newStyles.marginBottom || 12;

    // Очищаем кэш и обновляем координаты
    this._clearCache();
    this.setCoords();

    // Уведомляем метабуллит об изменении уровня
    this.notifyMetaBulletChanged('level_changed', {
      oldLevel: this.bulletLevel,
      newLevel: validLevel,
      oldType: this.entityType,
      newType: newBulletType,
    });
  }

  /**
   * Переопределение события изменения текста для интеграции с метабуллитами
   * Вызывается автоматически при изменении содержимого текста
   */
  _onTextChanged(): void {
    // Вызываем родительский метод для стандартной обработки
    if (super._onTextChanged) {
      super._onTextChanged();
    }

    // ✅ Блокируем событие для новых сущностей (созданных через Enter, но еще не заполненных)
    // Бекап будет создан только при выходе из редактирования, если текст был изменен
    if ((this as any)._isNewlyCreated) {
      return;
    }

    // Уведомляем метабуллит об изменении текста
    this.notifyMetaBulletChanged('text_changed', {
      newText: this.text,
      textLength: this.text?.length || 0,
    });
  }

  /**
   * Проверяет, находится ли курсор в конце текста
   */
  private _isCursorAtEnd(): boolean {
    // В Fabric.js Textbox есть свойство selectionEnd для определения позиции курсора
    const textLength = (this.text || '').length;

    // Получаем позицию курсора из всех возможных источников
    const cursorPosition = this.selectionEnd ?? this.selectionStart ?? 0;

    console.warn('🔍🔍🔍 ДЕТАЛЬНАЯ ПРОВЕРКА ПОЗИЦИИ КУРСОРА:', {
      textLength,
      text: this.text,
      selectionStart: this.selectionStart,
      selectionEnd: this.selectionEnd,
      cursorPosition,
      isAtEnd: cursorPosition >= textLength,
      // Проверяем дополнительные свойства Fabric.js
      hiddenTextarea: (this as any).hiddenTextarea?.selectionStart,
      hiddenTextareaEnd: (this as any).hiddenTextarea?.selectionEnd,
    });

    return cursorPosition >= textLength;
  }

  /**
   * Создает дубликат текущей сущности в том же метабуллите
   */
  private _createEntityDuplicate(): void {
    // ✅ Устанавливаем флаг что мы создаем новую сущность - это блокирует бекап для старой сущности
    (this as any)._isCreatingNewEntity = true;

    if (!this.canvas || !this.metaBulletId) {
      console.error('❌ Невозможно создать дубликат: отсутствует canvas или metaBulletId', {
        canvas: !!this.canvas,
        metaBulletId: this.metaBulletId,
        entityId: this.id,
        entityType: this.entityType,
      });
      return;
    }

    // Получаем доступ к MetaBulletSystem
    const system = (this.canvas as any)._metaBulletSystem;
    if (!system) {
      console.error('❌ MetaBulletSystem не найден на canvas');
      return;
    }

    // Проверяем, существует ли метабуллит
    const metaBullet = system.getMetaBullet(this.metaBulletId);
    if (!metaBullet) {
      console.error('❌ MetaBullet не найден:', {
        searchedMetaBulletId: this.metaBulletId,
        availableMetaBullets: system.getAllMetaBullets().map((mb: any) => ({
          id: mb.id,
          entitiesCount: mb.entityIds.length,
        })),
      });
      return;
    }

    try {
      // Создаем новую сущность с тем же типом в том же метабуллите
      // Пропускаем начальную рекалькуляцию т.к. маркер будет скопирован позже
      const newEntity = system.createEntityInMetaBullet(
        this.metaBulletId, // Тот же метабуллит
        this.id, // После текущей сущности
        '', // Пустой текст
        this.entityType, // Тот же тип
        true, // skipInitialRecalculation - маркер будет добавлен позже
      );

      // ✅ Флаг уже установлен в createEntityInMetaBullet ДО добавления на canvas
      // Проверяем что флаг установлен (для отладки)
      if (!(newEntity as any)._isNewlyCreated) {
        console.warn('⚠️ Флаг _isNewlyCreated не установлен для новой сущности!');
        (newEntity as any)._isNewlyCreated = true;
      }

      // Проверяем, что новая сущность правильно создана
      if (!newEntity.metaBulletId) {
        console.error('❌ Новая сущность не имеет metaBulletId!');
        return;
      }

      if (newEntity.metaBulletId !== this.metaBulletId) {
        console.error('❌ Новая сущность имеет неправильный metaBulletId!', {
          expected: this.metaBulletId,
          actual: newEntity.metaBulletId,
        });
        return;
      }

      // Через небольшую задержку переходим в режим редактирования новой сущности
      setTimeout(() => {
        if (this.canvas && newEntity.canvas) {
          // Проверяем, что новая сущность все еще существует на canvas
          const objectsOnCanvas = this.canvas.getObjects();
          const newEntityOnCanvas = objectsOnCanvas.find((obj) => obj.id === newEntity.id);

          if (!newEntityOnCanvas) {
            console.error('❌ Новая сущность не найдена на canvas после создания!', {
              searchedId: newEntity.id,
              objectsOnCanvas: objectsOnCanvas.map((obj) => ({ id: obj.id, type: obj.type })),
            });
            return;
          }

          // Снимаем активность с текущей сущности
          this.canvas.discardActiveObject();

          // Копируем маркеры с исходной сущности на новую
          // После копирования вызываем рекалькуляцию с правильной высотой маркера
          setTimeout(() => {
            // Пропускаем авто-рекалькуляцию - вызовем вручную после
            this.copyMarkersToNewEntity(newEntity.id, (this.canvas as any)?.id, true);

            // Рекалькуляция ПОСЛЕ копирования маркера (с задержкой для добавления на canvas)
            setTimeout(() => {
              metaBullet.recalculatePositions(true);

              // ✅ Сбрасываем excludeFromBackup для маркеров и контента после рекалькуляции
              // Теперь они могут участвовать в бекапах
              setTimeout(() => {
                const allMarkers = this.canvas!.getObjects().filter(
                  (obj) => obj.type === 'marker',
                ) as Marker[];
                allMarkers.forEach((marker) => {
                  if (marker.entityId === newEntity.id) {
                    marker.excludeFromBackup = false;
                    // Сбрасываем флаг для всех контент-объектов маркера
                    // Используем canvas для получения объектов маркера
                    const markerContentObjects = this.canvas!.getObjects().filter(
                      (obj) => (obj as unknown as { markerId?: string }).markerId === marker.id,
                    );
                    markerContentObjects.forEach((content) => {
                      content.excludeFromBackup = false;
                    });
                  }
                });
              }, 50);
              // ⚠️ НЕ создаем бекап здесь - бекап будет создан только при выходе из редактирования,
              // если пользователь действительно заполнил текст (или при удалении пустой сущности)
            }, 100); // Даем время маркеру добавиться на canvas
          }, 50);

          // ✅ Сбрасываем флаг после перехода в редактирование новой сущности
          (this as any)._isCreatingNewEntity = false;

          // Активируем новую сущность и переходим в режим редактирования
          this.canvas.setActiveObject(newEntity);
          newEntity.enterEditing();

          // Выделяем весь текст (пустой, но для удобства)
          newEntity.selectAll();
        } else {
          console.error('❌ Canvas не доступен для перехода в режим редактирования', {
            canvas: !!this.canvas,
            newEntityCanvas: !!newEntity.canvas,
          });
        }
      }, 150); // Небольшая задержка для завершения анимации пересчета позиций
    } catch (error) {
      console.error('❌ Ошибка при создании дубликата сущности:', error);
    }
  }

  /**
   * Разделяет текст на две сущности в позиции курсора
   * @param cursorPosition - позиция курсора для разделения
   */
  public _splitTextIntoTwoEntities(cursorPosition: number): void {
    if (!this.canvas || !this.metaBulletId) {
      console.warn('⚠️ Невозможно разделить текст: отсутствует canvas или metaBulletId');
      return;
    }

    const originalText = this.text || '';
    const textBefore = originalText.substring(0, cursorPosition);
    const textAfter = originalText.substring(cursorPosition);

    // Получаем доступ к MetaBulletSystem
    const system = this.canvas._metaBulletSystem;
    if (!system) {
      console.warn('⚠️ MetaBulletSystem не найден на canvas');
      return;
    }

    // Устанавливаем флаг что мы в процессе split (блокирует modified event)
    (this as any)._isSplitting = true;

    // Создаем новую сущность со второй частью текста СНАЧАЛА
    // Пропускаем начальную рекалькуляцию т.к. маркер будет скопирован позже
    const newEntity = system.createEntityInMetaBullet(
      this.metaBulletId,
      this.id, // После текущей сущности
      textAfter, // Вторая часть текста
      this.entityType, // Тот же тип
      true, // skipInitialRecalculation - маркер будет добавлен позже
    );

    // Оставляем первую часть в текущей сущности (событие modified заблокировано флагом)
    this.set('text', textBefore);

    // Копируем маркеры с исходной сущности на новую
    // После копирования вызываем рекалькуляцию с правильной высотой маркера
    const metaBullet = system.getMetaBullet(this.metaBulletId);

    setTimeout(() => {
      // Пропускаем авто-рекалькуляцию - вызовем вручную после
      this.copyMarkersToNewEntity(newEntity.id, (this.canvas as any)?.id, true);

      // Рекалькуляция ПОСЛЕ копирования маркера (с задержкой для добавления на canvas)
      if (metaBullet) {
        setTimeout(() => {
          metaBullet.recalculatePositions(true);

          // Снимаем флаг _isSplitting ПОСЛЕ всех операций
          (this as any)._isSplitting = false;
        }, 100); // Даем время маркеру добавиться на canvas
      } else {
        // Если metaBullet не найден - снимаем флаг сразу
        (this as any)._isSplitting = false;
        console.warn('⚠️ MetaBullet не найден, снимаем флаг _isSplitting');
      }
    }, 50);

    // Переходим в режим редактирования новой сущности
    setTimeout(() => {
      if (this.canvas && newEntity.canvas) {
        this.canvas.discardActiveObject();
        this.canvas.setActiveObject(newEntity);
        newEntity.enterEditing();
        // Устанавливаем курсор в начало новой сущности
        newEntity.selectionStart = 0;
        newEntity.selectionEnd = 0;
      }
    }, 100);
  }

  /**
   * Удаляет все связанные маркеры и их контент с канваса
   * Принимает canvas как параметр для случаев когда this.canvas уже undefined
   */
  public removeLinkedMarkersWithCanvas(canvas: Canvas): void {
    if (!canvas) {
      return;
    }

    // Находим маркеры связанные с этой сущностью
    const linkedMarkers = canvas.getObjects().filter((obj: any) => {
      return obj.type === 'marker' && obj.entityId === this.id;
    });

    if (linkedMarkers.length === 0) {
      return;
    }

    linkedMarkers.forEach((marker: any) => {
      // Находим контент маркера по markerId (НО НЕ саму TextEntity!)
      const contentObjects = canvas
        .getObjects()
        .filter((obj: any) => obj.markerId === marker.id && !TextEntity.isTextEntity(obj));

      // Удаляем контент маркера
      contentObjects.forEach((contentObj) => {
        canvas.remove(contentObj);

        //  Удаляем контент маркера из layersMap
        try {
          const canvasStore = useCanvasStore.getState();
          const activeCanvasId = canvasStore.activeCanvasId;

          if (activeCanvasId && contentObj.layer) {
            canvasStore.removeLayerObject(activeCanvasId, contentObj.layer, {
              object: contentObj,
              zIndex: contentObj.zIndex || 0,
              layer: contentObj.layer,
            });
          }
        } catch (error) {
          console.warn('⚠️ Не удалось удалить контент маркера из layersMap:', error);
        }
      });

      // Удаляем сам маркер
      canvas.remove(marker);

      //  Удаляем маркер из layersMap
      try {
        const canvasStore = useCanvasStore.getState();
        const activeCanvasId = canvasStore.activeCanvasId;

        if (activeCanvasId && marker.layer) {
          canvasStore.removeLayerObject(activeCanvasId, marker.layer, {
            object: marker,
            zIndex: marker.zIndex || 0,
            layer: marker.layer,
          });
        }
      } catch (error) {
        console.warn('⚠️ Не удалось удалить маркер из layersMap:', error);
      }
    });

    if (linkedMarkers.length > 0) {
      canvas.requestRenderAll();
    }
  }

  /**
   * Удаляет все связанные маркеры и их контент с канваса
   * (обертка для случаев когда this.canvas доступен)
   */
  public removeLinkedMarkers(): void {
    if (!this.canvas) {
      return;
    }
    this.removeLinkedMarkersWithCanvas(this.canvas);
  }

  /**
   * Удаляет пустую сущность и переключается на предыдущую
   */
  public _deleteEmptyEntityAndFocusPrevious(): void {
    if (!this.canvas || !this.metaBulletId) {
      console.warn('⚠️ Невозможно удалить сущность: отсутствует canvas или metaBulletId');
      return;
    }

    // === СОХРАНЯЕМ ССЫЛКИ ДО УДАЛЕНИЯ ===
    // ВАЖНО: сохраняем ссылку на canvas и system ДО удаления сущности,
    // так как после удаления this.canvas станет undefined
    const canvasRef = this.canvas;
    const system = canvasRef._metaBulletSystem;

    if (!system) {
      console.warn('⚠️ MetaBulletSystem не найден на canvas');
      return;
    }

    // ⚠️ КРИТИЧЕСКИ ВАЖНО: получаем ID предыдущей сущности В САМОМ НАЧАЛЕ
    // ДО любых вызовов safeDelete() и removeLinkedMarkers()
    // Иначе события удаления могут удалить сущность из MetaBullet раньше
    const metaBullet = system.getMetaBullet(this.metaBulletId);
    let previousEntityId: string | null = null;

    console.debug('🔍 Ищем предыдущую сущность. metaBulletId:', this.metaBulletId);
    console.debug('🔍 MetaBullet найден:', metaBullet ? 'ДА' : 'НЕТ');

    if (metaBullet) {
      const entities = metaBullet.getEntities();
      console.debug('🔍 Всего сущностей в MetaBullet:', entities.length);
      console.debug(
        '🔍 ID сущностей:',
        entities.map((e: TextEntity) => e.id),
      );
      console.debug('🔍 Текущая сущность ID:', this.id);

      const currentIndex = entities.findIndex((e: TextEntity) => e.id === this.id);
      console.debug('🔍 Индекс текущей сущности:', currentIndex);

      const previousEntity = currentIndex > 0 ? entities[currentIndex - 1] : null;
      console.debug('🔍 Предыдущая сущность:', previousEntity ? previousEntity.id : 'НЕТ');

      previousEntityId = previousEntity?.id || null;
      console.debug('🔍 previousEntityId:', previousEntityId);
    }

    // 🔢 УДАЛЯЕМ СВЯЗАННЫЙ SUPSCRIPT ПЕРЕД УДАЛЕНИЕМ DIGIT
    // Вызываем safeDelete() для удаления связанного superscript, если это digit
    this.safeDelete();

    // Удаляем связанные маркеры ПЕРЕД удалением самой сущности
    // Используем сохраненную ссылку на canvas
    this.removeLinkedMarkersWithCanvas(canvasRef);

    // Удаляем сущность из MetaBullet и с canvas
    // НО теперь мы уже получили previousEntityId заранее
    system.removeEntityFromMetaBullet(this.id);

    // Переключаемся на предыдущую сущность, если она есть
    if (previousEntityId && canvasRef) {
      console.debug('🔄 Начинаем переключение на предыдущую сущность:', previousEntityId);

      // Используем requestAnimationFrame для выполнения после завершения текущего цикла событий
      requestAnimationFrame(() => {
        // Используем сохраненную ссылку на canvas
        const previousEntity = canvasRef
          .getObjects()
          .find((obj) => obj.id === previousEntityId) as TextEntity;

        if (previousEntity) {
          // Сначала отменяем выделение текущих объектов
          canvasRef.discardActiveObject();

          // Затем активируем предыдущую сущность
          canvasRef.setActiveObject(previousEntity);

          // Получаем длину текста ДО входа в режим редактирования
          const textLength = (previousEntity.text || '').length;

          // Входим в режим редактирования
          previousEntity.enterEditing();

          // Устанавливаем позицию курсора СРАЗУ после enterEditing()
          // Используем синхронный код для немедленной установки, чтобы избежать визуального "прыжка"
          const hiddenTextarea = (previousEntity as any).hiddenTextarea;

          if (hiddenTextarea) {
            // Синхронизируем значение текста
            hiddenTextarea.value = previousEntity.text || '';
            // Устанавливаем курсор в конец СРАЗУ
            hiddenTextarea.selectionStart = textLength;
            hiddenTextarea.selectionEnd = textLength;
            // Фокусируемся
            hiddenTextarea.focus();
          }

          // Также устанавливаем позицию в объекте TextEntity
          previousEntity.selectionStart = textLength;
          previousEntity.selectionEnd = textLength;

          // Дополнительная проверка через requestAnimationFrame на случай если hiddenTextarea еще не создан
          requestAnimationFrame(() => {
            const finalHiddenTextarea = (previousEntity as any).hiddenTextarea;
            if (finalHiddenTextarea && previousEntity.isEditing) {
              // Убеждаемся что курсор все еще в конце (на случай если что-то сбросило позицию)
              const currentStart = finalHiddenTextarea.selectionStart;
              const currentEnd = finalHiddenTextarea.selectionEnd;
              if (currentStart !== textLength || currentEnd !== textLength) {
                console.debug('⚠️ Позиция курсора была изменена, восстанавливаем в конец');
                finalHiddenTextarea.selectionStart = textLength;
                finalHiddenTextarea.selectionEnd = textLength;
              }
            }

            // Перерисовываем canvas
            canvasRef.requestRenderAll();

            console.debug('✅ Переключение на предыдущую сущность завершено');
          });
        } else {
          console.warn('⚠️ Предыдущая сущность не найдена на canvas:', previousEntityId);
        }
      });
    } else {
      console.debug('ℹ️ Нет предыдущей сущности для переключения');
    }
  }

  /**
   * Удаляет пустую сущность и переключается на следующую (для Enter)
   */
  public _deleteEmptyEntityAndFocusNext(): void {
    if (!this.canvas || !this.metaBulletId) {
      console.warn('⚠️ Невозможно удалить сущность: отсутствует canvas или metaBulletId');
      return;
    }

    // === СОХРАНЯЕМ ССЫЛКИ ДО УДАЛЕНИЯ ===
    const canvasRef = this.canvas;
    const system = canvasRef._metaBulletSystem;

    if (!system) {
      console.warn('⚠️ MetaBulletSystem не найден на canvas');
      return;
    }

    // КРИТИЧЕСКИ ВАЖНО: получаем ID следующей сущности ДО удаления этой сущности
    const metaBullet = system.getMetaBullet(this.metaBulletId);
    let nextEntityId: string | null = null;

    if (metaBullet) {
      const entities = metaBullet.getEntities();
      const currentIndex = entities.findIndex((entity: any) => entity.id === this.id);
      const nextEntity =
        currentIndex !== -1 && currentIndex < entities.length - 1
          ? entities[currentIndex + 1]
          : null;
      nextEntityId = nextEntity?.id || null;
    }

    // Удаляем связанные маркеры (если есть)
    this.removeLinkedMarkersWithCanvas(canvasRef);

    // Удаляем сущность из системы
    system.removeEntityFromMetaBullet(this.id);

    // Переключаемся на следующую сущность, если она есть
    if (nextEntityId && canvasRef) {
      setTimeout(() => {
        const nextEntity = canvasRef
          .getObjects()
          .find((obj) => obj.id === nextEntityId) as TextEntity;

        if (nextEntity) {
          canvasRef.discardActiveObject();
          canvasRef.setActiveObject(nextEntity);
          nextEntity.enterEditing();

          // Устанавливаем курсор в конец следующей сущности
          setTimeout(() => {
            const textLength = (nextEntity.text || '').length; // Курсор в конец
            if (nextEntity.isEditing && typeof nextEntity.setSelectionStart === 'function') {
              nextEntity.setSelectionStart(textLength);
              nextEntity.setSelectionEnd(textLength);
            } else {
              nextEntity.selectionStart = textLength;
              nextEntity.selectionEnd = textLength;
            }
          }, 100);
        } else {
          console.warn('⚠️ Следующая сущность не найдена на canvas:', nextEntityId);
        }
      }, 50);
    }
  }

  /**
   * Переключиться только на предыдущую сущность БЕЗ удаления текущей (для Backspace)
   */
  public _focusPreviousEntityOnly(): void {
    if (!this.canvas || !this.metaBulletId) {
      console.warn('⚠️ Невозможно переключиться: отсутствует canvas или metaBulletId');
      return;
    }

    // Получаем MetaBulletSystem и текущий MetaBullet
    const canvasRef = this.canvas;
    const system = canvasRef._metaBulletSystem;

    if (!system) {
      console.warn('⚠️ MetaBulletSystem не найден на canvas');
      return;
    }

    const metaBullet = system.getMetaBullet(this.metaBulletId);
    if (!metaBullet) {
      console.warn('⚠️ MetaBullet не найден:', this.metaBulletId);
      return;
    }

    // Получаем все сущности в правильном порядке
    const entities = metaBullet.getEntities();
    const currentIndex = entities.findIndex((entity: any) => entity.id === this.id);

    if (currentIndex === -1) {
      console.warn('⚠️ Текущая сущность не найдена в MetaBullet:', this.id);
      return;
    }

    // Ищем предыдущую сущность
    const previousEntityId = currentIndex > 0 ? entities[currentIndex - 1].id : null;

    if (previousEntityId) {
      // Есть предыдущая сущность - переключаемся на неё
      // Используем requestAnimationFrame для выполнения после завершения текущего цикла событий
      requestAnimationFrame(() => {
        // Ищем предыдущую сущность на canvas
        const previousEntity = canvasRef
          .getObjects()
          .find((obj) => obj.id === previousEntityId) as TextEntity;

        if (previousEntity) {
          // Получаем длину текста ДО входа в режим редактирования
          const textLength = (previousEntity.text || '').length;

          canvasRef.discardActiveObject();
          canvasRef.setActiveObject(previousEntity);
          previousEntity.enterEditing();

          // Устанавливаем позицию курсора СРАЗУ после enterEditing()
          // Используем синхронный код для немедленной установки, чтобы избежать визуального "прыжка"
          const hiddenTextarea = (previousEntity as any).hiddenTextarea;

          if (hiddenTextarea) {
            // Синхронизируем значение текста
            hiddenTextarea.value = previousEntity.text || '';
            // Устанавливаем курсор в конец СРАЗУ
            hiddenTextarea.selectionStart = textLength;
            hiddenTextarea.selectionEnd = textLength;
            // Фокусируемся
            hiddenTextarea.focus();
          }

          // Также устанавливаем позицию в объекте TextEntity
          previousEntity.selectionStart = textLength;
          previousEntity.selectionEnd = textLength;

          // Дополнительная проверка через requestAnimationFrame на случай если hiddenTextarea еще не создан
          requestAnimationFrame(() => {
            const finalHiddenTextarea = (previousEntity as any).hiddenTextarea;
            if (finalHiddenTextarea && previousEntity.isEditing) {
              // Убеждаемся что курсор все еще в конце (на случай если что-то сбросило позицию)
              const currentStart = finalHiddenTextarea.selectionStart;
              const currentEnd = finalHiddenTextarea.selectionEnd;
              if (currentStart !== textLength || currentEnd !== textLength) {
                finalHiddenTextarea.selectionStart = textLength;
                finalHiddenTextarea.selectionEnd = textLength;
              }
            }

            // Перерисовываем canvas
            canvasRef.requestRenderAll();
          });
        } else {
          console.warn('⚠️ Предыдущая сущность не найдена на canvas:', previousEntityId);
        }
      });
    } else {
      // Нет предыдущей сущности - остаемся на месте
    }
  }

  /**
   * Навигация к предыдущей сущности (для стрелок вверх/влево)
   * Курсор устанавливается в конец предыдущей сущности
   */
  private _navigateToPreviousEntity(): void {
    if (!this.canvas || !this.metaBulletId) {
      return;
    }

    const canvasRef = this.canvas;
    const system = canvasRef._metaBulletSystem;

    if (!system) {
      return;
    }

    const metaBullet = system.getMetaBullet(this.metaBulletId);
    if (!metaBullet) {
      return;
    }

    const entities = metaBullet.getEntities();
    const currentIndex = entities.findIndex((entity: any) => entity.id === this.id);

    if (currentIndex === -1) {
      return;
    }

    // Ищем предыдущую сущность
    const previousEntityId = currentIndex > 0 ? entities[currentIndex - 1].id : null;

    if (previousEntityId) {
      requestAnimationFrame(() => {
        const previousEntity = canvasRef
          .getObjects()
          .find((obj) => obj.id === previousEntityId) as TextEntity;

        if (previousEntity) {
          // Получаем длину текста ДО входа в режим редактирования
          const textLength = (previousEntity.text || '').length;

          this.exitEditing();
          canvasRef.discardActiveObject();
          canvasRef.setActiveObject(previousEntity);
          previousEntity.enterEditing();

          // Устанавливаем курсор в КОНЕЦ предыдущей сущности
          const hiddenTextarea = (previousEntity as any).hiddenTextarea;

          if (hiddenTextarea) {
            hiddenTextarea.value = previousEntity.text || '';
            hiddenTextarea.selectionStart = textLength;
            hiddenTextarea.selectionEnd = textLength;
            hiddenTextarea.focus();
          }

          previousEntity.selectionStart = textLength;
          previousEntity.selectionEnd = textLength;

          requestAnimationFrame(() => {
            const finalHiddenTextarea = (previousEntity as any).hiddenTextarea;
            if (finalHiddenTextarea && previousEntity.isEditing) {
              const currentStart = finalHiddenTextarea.selectionStart;
              const currentEnd = finalHiddenTextarea.selectionEnd;
              if (currentStart !== textLength || currentEnd !== textLength) {
                finalHiddenTextarea.selectionStart = textLength;
                finalHiddenTextarea.selectionEnd = textLength;
              }
            }

            canvasRef.requestRenderAll();
          });
        }
      });
    }
  }

  /**
   * Навигация к следующей сущности (для стрелок вниз/вправо)
   * Курсор устанавливается в начало следующей сущности
   */
  private _navigateToNextEntity(): void {
    if (!this.canvas || !this.metaBulletId) {
      return;
    }

    const canvasRef = this.canvas;
    const system = canvasRef._metaBulletSystem;

    if (!system) {
      return;
    }

    const metaBullet = system.getMetaBullet(this.metaBulletId);
    if (!metaBullet) {
      return;
    }

    const entities = metaBullet.getEntities();
    const currentIndex = entities.findIndex((entity: any) => entity.id === this.id);

    if (currentIndex === -1) {
      return;
    }

    // Ищем следующую сущность
    const nextEntityId = currentIndex < entities.length - 1 ? entities[currentIndex + 1].id : null;

    if (nextEntityId) {
      requestAnimationFrame(() => {
        const nextEntity = canvasRef
          .getObjects()
          .find((obj) => obj.id === nextEntityId) as TextEntity;

        if (nextEntity) {
          this.exitEditing();
          canvasRef.discardActiveObject();
          canvasRef.setActiveObject(nextEntity);
          nextEntity.enterEditing();

          // Устанавливаем курсор в НАЧАЛО следующей сущности
          const hiddenTextarea = (nextEntity as any).hiddenTextarea;

          if (hiddenTextarea) {
            hiddenTextarea.value = nextEntity.text || '';
            hiddenTextarea.selectionStart = 0;
            hiddenTextarea.selectionEnd = 0;
            hiddenTextarea.focus();
          }

          nextEntity.selectionStart = 0;
          nextEntity.selectionEnd = 0;

          requestAnimationFrame(() => {
            const finalHiddenTextarea = (nextEntity as any).hiddenTextarea;
            if (finalHiddenTextarea && nextEntity.isEditing) {
              const currentStart = finalHiddenTextarea.selectionStart;
              const currentEnd = finalHiddenTextarea.selectionEnd;
              if (currentStart !== 0 || currentEnd !== 0) {
                finalHiddenTextarea.selectionStart = 0;
                finalHiddenTextarea.selectionEnd = 0;
              }
            }

            canvasRef.requestRenderAll();
          });
        }
      });
    }
  }

  /**
   * Проверяет и удаляет сущность, если она пустая после выхода из редактирования
   */
  public _checkAndRemoveIfEmpty(): void {
    const isEmpty = !this.text || this.text.trim().length === 0;

    if (isEmpty) {
      // Проверяем, что сущность все еще на canvas (не была удалена)
      const isStillOnCanvas = this.canvas?.getObjects().includes(this);
      if (isStillOnCanvas) {
        this._deleteEmptyEntityAndFocusPrevious();
      }
    }
  }

  /**
   * Переопределение события изменения размеров для пересчета позиций
   * Вызывается при изменении размеров текстового блока
   */
  _onSizeChange(): void {
    // Проверяем есть ли родительский метод
    if (super._onSizeChange) {
      super._onSizeChange();
    }

    // Если это digit в digital-accent, обновляем позицию superscript
    if (this.isDigitalAccentDigit()) {
      this.updateLinkedAccentPosition();
    }

    // Уведомляем метабуллит об изменении размеров
    this.notifyMetaBulletChanged('size_changed', {
      newWidth: this.width,
      newHeight: this.height,
    });
  }

  /**
   * Обработчик изменения позиции для пересчета метабуллита
   * Вызывается при перемещении объекта (drag & drop)
   */
  _onPositionChanged(): void {
    // Если это digit в digital-accent, обновляем позицию superscript
    if (this.isDigitalAccentDigit()) {
      this.updateLinkedAccentPosition();
    }

    // Уведомляем метабуллит об изменении позиции
    this.notifyMetaBulletChanged('position_changed', {
      newLeft: this.left,
      newTop: this.top,
    });
  }

  /**
   * Уведомить метабуллит об изменениях в сущности
   * @param eventType - Тип события
   * @param data - Дополнительные данные события
   */
  private notifyMetaBulletChanged(eventType: string, data?: any): void {
    if (!this.canvas) {
      return;
    }

    // Проверяем есть ли система MetaBulletSystem на canvas
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const system = (this.canvas as any)._metaBulletSystem;

    // Если сущность перетаскивается за границы своего метабуллита, не отправляем уведомления
    // Это предотвращает ненужную рекалькуляцию старого метабуллита
    if (
      system &&
      system.currentDraggedEntity &&
      system.currentDraggedEntity.id === this.id &&
      system.isCurrentlyDragOutOfBounds &&
      eventType === 'entity_modified'
    ) {
      return;
    }

    // Временно исключаем объект из бекапа во время обработки метабуллитом
    // Это предотвращает создание промежуточных бекапов во время анимаций
    this.excludeFromBackup = true;

    const eventData = {
      entityId: this.id,
      metaBulletId: this.metaBulletId,
      entityType: this.entityType,
      entityOrder: this.entityOrder,
      eventType,
      timestamp: Date.now(),
      data: data || {},
    };

    // Отправляем кастомное событие через canvas
    this.canvas.fire('metaBullet:entity:changed', eventData);
  }

  /**
   * Восстановить возможность создания бекапов для объекта
   * Вызывается после завершения обработки метабуллитом
   */
  enableBackup(): void {
    this.excludeFromBackup = false;
  }

  /**
   * Обновить конфигурацию стилей
   * Позволяет изменить стили для всех типов сущностей на лету
   */
  updateStylesConfig(newStylesConfig: Partial<MetaBulletStylesConfig>): void {
    // Обновляем конфигурацию стилей
    this.stylesConfig = mergeStylesConfig(newStylesConfig);

    // Применяем новые стили к текущему типу сущности
    const currentStyles = this.getStylesForType(this.entityType);

    // Сохраняем текущие позицию и размеры
    const currentLeft = this.left;
    const currentTop = this.top;
    const currentWidth = this.width;
    const currentHeight = this.height;

    // Применяем новые стили
    this.set({
      fontSize: currentStyles.fontSize,
      fontFamily: currentStyles.fontFamily,
      fontWeight: currentStyles.fontWeight,
      lineHeight: currentStyles.lineHeight,
      // Сохраняем позицию и размеры
      left: currentLeft,
      top: currentTop,
      width: currentWidth,
      height: currentHeight,
    });

    // Обновляем отступы
    this.marginBottom = currentStyles.marginBottom || 16;

    // Если это буллит, пересчитываем marginLeft - берем из стилей (НОВАЯ СИСТЕМА v7.0)
    if (isBulletType(this.entityType)) {
      this.marginLeft = currentStyles.marginLeft || 0;
    } else {
      this.marginLeft = currentStyles.marginLeft || 0;
    }

    // Очищаем кэш и обновляем координаты
    this._clearCache();
    this.setCoords();

    // 🔢 Обновляем позицию digital-accent после применения стилей
    if (this.isDigitalAccentDigit()) {
      // Используем requestAnimationFrame чтобы дать время на применение стилей
      requestAnimationFrame(() => {
        this.updateLinkedAccentPosition();
      });
    }
  }

  /**
   * Получить текущую конфигурацию стилей
   */
  getStylesConfig(): MetaBulletStylesConfig {
    return { ...this.stylesConfig };
  }

  /**
   * Обновить стили из централизованной конфигурации канваса
   * Этот метод вызывается при загрузке объектов из бэкенда
   */
  updateStylesFromCanvasConfig(canvasConfig?: {
    textEntityStyles?: Partial<MetaBulletStylesConfig>;
  }): void {
    if (canvasConfig?.textEntityStyles) {
      // Обновляем конфигурацию стилей
      this.stylesConfig = mergeStylesConfig(canvasConfig.textEntityStyles);

      // Применяем новые стили к текущему типу сущности
      const currentStyles = this.getStylesForType(this.entityType);

      // Сохраняем текущие позицию и размеры
      const currentLeft = this.left;
      const currentTop = this.top;
      const currentWidth = this.width;
      const currentHeight = this.height;

      // Применяем новые стили
      this.set({
        fontSize: currentStyles.fontSize,
        fontFamily: currentStyles.fontFamily,
        fontWeight: currentStyles.fontWeight,
        lineHeight: currentStyles.lineHeight,
        // Сохраняем позицию и размеры
        left: currentLeft,
        top: currentTop,
        width: currentWidth,
        height: currentHeight,
      });

      // Обновляем отступы
      this.marginBottom = currentStyles.marginBottom || 16;

      // Если это буллит, пересчитываем marginLeft
      if (isBulletType(this.entityType)) {
        this.marginLeft = this.calculateIndentForLevel(
          this.bulletLevel || getBulletLevel(this.entityType),
        );
      } else {
        this.marginLeft = currentStyles.marginLeft || 0;
      }

      // Очищаем кэш и обновляем координаты
      this._clearCache();
      this.setCoords();

      // 🔢 Обновляем позицию digital-accent после применения стилей
      if (this.isDigitalAccentDigit()) {
        // Используем requestAnimationFrame чтобы дать время на применение стилей
        requestAnimationFrame(() => {
          this.updateLinkedAccentPosition();
        });
      }
    }
  }

  // === МЕТОДЫ ДЛЯ РАБОТЫ С МАРКЕРАМИ ===

  /**
   * Ищет существующий маркер на канвасе для данной текстовой сущности
   */
  private findExistingMarkerOnCanvas(): Marker | undefined {
    if (!this.canvas) return undefined;

    const allObjects = this.canvas.getObjects();
    const existingMarker = allObjects.find(
      (obj: any) => obj.type === 'marker' && obj.entityId === this.id,
    ) as Marker | undefined;

    return existingMarker;
  }

  /**
   * @deprecated Метод удален - маркеры теперь всегда передаются с бэкенда как отдельные объекты
   */

  /**
   * Добавляет маркер на канвас
   * @deprecated Метод устарел - маркеры добавляются через loadObjectsWithLayers
   */
  private addMarkerToCanvas(): void {
    if (this.marker && this.canvas) {
      // Добавляем маркер на канвас
      this.canvas.add(this.marker);

      // Обновляем z-index маркера чтобы он был на том же уровне что и текст
      this.marker.zIndex = (this.zIndex || 0) - 1; // Маркер чуть ниже текста

      // ✅ ВАЖНО: Добавляем маркер в layers store
      this.addMarkerToLayersStore();

      // Вызываем onCanvas для добавления контента маркера
      this.marker.onCanvas();

      // Обновляем позицию маркера
      this.updateMarkerPosition();
    }
  }

  /**
   * Добавляет маркер в layers store
   */
  private addMarkerToLayersStore(): void {
    if (!this.marker || !this.canvas) return;

    try {
      const canvasStore = useCanvasStore.getState();
      const activeCanvasId = canvasStore.activeCanvasId;

      if (activeCanvasId) {
        canvasStore.addLayerObject(activeCanvasId, this.marker.layer, {
          object: this.marker,
          zIndex: this.marker.zIndex || 0,
          layer: this.marker.layer,
        });
      }
    } catch (error) {
      console.warn('⚠️ Failed to add marker to layers store:', error);
    }
  }

  /**
   * Удаляет маркер с канваса
   */
  private removeMarkerFromCanvas(): void {
    if (this.marker && this.canvas) {
      // ✅ Сначала удаляем из layers store (безопасно)
      this.removeMarkerFromLayersStore();

      // Вызываем onRemoved для удаления контента маркера
      this.marker.onRemoved();

      // Удаляем с канваса
      this.canvas.remove(this.marker);
    }
  }

  /**
   * Удаляет маркер из layers store (безопасно)
   */
  private removeMarkerFromLayersStore(): void {
    if (!this.marker) return;

    try {
      const canvasStore = useCanvasStore.getState();
      const activeCanvasId = canvasStore.activeCanvasId;

      if (activeCanvasId) {
        // Проверяем что маркер есть в layers перед удалением
        const canvasState = canvasStore.getCanvasState(activeCanvasId);
        const layerObjects =
          canvasState?.layersMap?.[this.marker.layer as keyof typeof canvasState.layersMap];

        if (layerObjects && layerObjects.some((item: any) => item.object.id === this.marker?.id)) {
          canvasStore.removeLayerObject(activeCanvasId, this.marker.layer, {
            object: this.marker,
            zIndex: this.marker.zIndex,
            layer: this.marker.layer,
          });
        }
      }
    } catch (error) {
      console.warn('⚠️ Error removing marker from layers store:', error);
    }
  }

  /**
   * Очищает связь с маркером (для случаев когда маркер удаляется отдельно)
   */
  clearMarkerReference(): void {
    this.marker = undefined;
    this.markerId = undefined;
  }

  /**
   * Безопасное удаление TextEntity с очисткой всех связей
   */
  safeDelete(): void {
    // 🔢 УДАЛЯЕМ СВЯЗАННУЮ СУЩНОСТЬ DIGITAL-ACCENT
    // Если удаляется digit - удаляем superscript (степень без цифры не имеет смысла)
    // Если удаляется superscript - оставляем digit (цифра может существовать без степени)
    if (this.isDigitalAccent && this.isDigitalAccent() && this.isDigitalAccentDigit()) {
      const linkedEntity = this.getLinkedAccentEntity();
      if (linkedEntity && linkedEntity.isDigitalAccentSuperscript() && this.canvas) {
        // Разрываем связь перед удалением, чтобы избежать рекурсии
        linkedEntity.linkedAccentEntityId = undefined;
        this.linkedAccentEntityId = undefined;

        // Удаляем связанную сущность superscript с канваса
        // ВАЖНО: Не вызываем safeDelete() для linkedEntity, чтобы избежать рекурсии
        // Просто удаляем маркеры и уведомляем систему
        console.debug('🔢 Deleting linked superscript when digit is deleted:', {
          deletedDigit: this.id,
          deletedSuperscript: linkedEntity.id,
        });
        linkedEntity.removeLinkedMarkers();
        linkedEntity.notifyMetaBulletChanged('entity_delete');
        this.canvas.remove(linkedEntity);
      }
    }

    // Удаляем все связанные маркеры и их контент
    this.removeLinkedMarkers();

    // Уведомляем MetaBulletSystem об удалении
    this.notifyMetaBulletChanged('entity_delete');

    // Удаляем себя с канваса (canvas.remove будет вызван извне)
  }

  /**
   * Обновляет позицию маркера относительно текстовой сущности
   */
  /**
   * 🚀 ОПТИМИЗАЦИЯ: Синхронное обновление маркеров при рендеринге
   * Переопределяем метод рендеринга для мгновенного обновления маркеров
   */
  _render(ctx: CanvasRenderingContext2D): void {
    // Вызываем оригинальный рендеринг
    super._render(ctx);

    // Сразу же обновляем маркеры - это происходит синхронно с рендерингом текста!
    this.updateMarkerPositionSync();
  }

  /**
   * 🚀 ДОПОЛНИТЕЛЬНАЯ ОПТИМИЗАЦИЯ: Обновление маркеров при трансформации
   * Переопределяем set для перехвата всех изменений позиции
   */
  set(key: any, value?: any): this {
    const result = super.set(key, value);

    // Если изменились координаты или размеры - обновляем маркеры и digital-accent
    if (
      typeof key === 'object' ||
      key === 'left' ||
      key === 'top' ||
      key === 'width' ||
      key === 'height'
    ) {
      // Используем requestAnimationFrame для избежания блокировки
      requestAnimationFrame(() => {
        this.updateMarkerPositionSync();
        // Обновляем позицию связанной сущности digital-accent
        if (this.isDigitalAccentDigit()) {
          this.updateLinkedAccentPosition();
        }
      });
    }

    return result;
  }

  /**
   * Синхронное обновление позиций маркеров (без requestRenderAll)
   */
  public updateMarkerPositionSync(): void {
    // 🎯 КРИТИЧНО: Пропускаем если объект в ActiveSelection
    if (this.group?.type.toLowerCase() === 'activeselection') {
      return;
    }

    // Ищем все маркеры связанные с этой сущностью
    const allMarkers = this.getAllLinkedMarkers();

    if (allMarkers.length === 0 && !this.marker) {
      return; // Нет маркеров
    }

    const markerConfig = this.getMarkerConfig();
    if (!markerConfig) {
      return;
    }

    // 🎯 ИСПРАВЛЕНИЕ КООРДИНАТ: В ActiveSelection entity.left/top уже абсолютные!
    const entityLeft = this.left || 0;
    const entityTop = this.top || 0;

    // Тестируем разные способы получения координат
    let absoluteLeft = entityLeft;
    let absoluteTop = entityTop;

    if (this.group) {
      const groupType = this.group.type?.toLowerCase();
      const isActiveSelection = groupType === 'activeselection';

      // Для ActiveSelection НЕ складываем координаты!
      if (isActiveSelection) {
        absoluteLeft = entityLeft;
        absoluteTop = entityTop;
      } else {
        // Для других типов групп может быть нужно складывать
        absoluteLeft = (this.group.left || 0) + entityLeft;
        absoluteTop = (this.group.top || 0) + entityTop;
      }
    }

    // 🚀 ОПТИМИЗАЦИЯ: Кэшируем последние координаты для избежания лишних обновлений
    const cacheKey = `${absoluteLeft},${absoluteTop}`;
    if ((this as any)._lastMarkerUpdateKey === cacheKey) {
      return; // Позиция не изменилась
    }
    (this as any)._lastMarkerUpdateKey = cacheKey;

    // Обновляем все маркеры (включая this.marker если он есть)
    allMarkers.forEach((marker) => {
      // НОВАЯ КОНЦЕПЦИЯ: Маркер позиционируется СЛЕВА от текста с отступом (offsetX)
      const offsetX = markerConfig.position.offsetX || 0;
      const markerWidth = markerConfig.position.width;
      const markerHeight = markerConfig.position.height;

      // Рассчитываем координату ЛЕВОГО КРАЯ маркера
      const markerLeftEdge = absoluteLeft - markerWidth - offsetX;

      // Корректируем в зависимости от origin маркера
      const finalMarkerLeft =
        marker.originX === 'center'
          ? markerLeftEdge + markerWidth / 2 // Для center origin добавляем половину ширины
          : markerLeftEdge; // Для left origin оставляем как есть

      // Рассчитываем finalMarkerTop в зависимости от типа offsetY
      let markerTopEdge: number;
      const offsetY = markerConfig.position.offsetY;
      const entityHeight = this.height || 0;

      if (typeof offsetY === 'number') {
        markerTopEdge = absoluteTop + offsetY;
      } else if (offsetY === 'top') {
        markerTopEdge = absoluteTop;
      } else if (offsetY === 'center') {
        // Верхний край маркера для центрирования по вертикали
        markerTopEdge = absoluteTop + (entityHeight - markerHeight) / 2;
      } else if (offsetY === 'bottom') {
        // Верхняя граница маркера = нижняя граница текста минус высота маркера
        markerTopEdge = absoluteTop + entityHeight - markerHeight;
      } else if (offsetY === 'centered-by-first-line') {
        // Выравнивание по центру ПЕРВОЙ строки
        const fontSize = this.fontSize || 16;
        const lineHeight = this.lineHeight || 1.16;
        const firstLineHeight = fontSize * lineHeight;
        markerTopEdge = absoluteTop + (firstLineHeight - markerHeight) / 2;
      } else {
        markerTopEdge = absoluteTop;
      }

      // Корректируем в зависимости от originY маркера
      const finalMarkerTop =
        marker.originY === 'center'
          ? markerTopEdge + markerHeight / 2 // Для center origin добавляем половину высоты
          : markerTopEdge; // Для top origin оставляем как есть

      // Устанавливаем позицию маркера напрямую
      marker.set({
        left: finalMarkerLeft,
        top: finalMarkerTop,
        width: markerWidth,
        height: markerHeight,
      });
      marker.setCoords();

      // Синхронно обновляем контент маркера
      if (marker.adjustContentObjectToContainerSync) {
        marker.adjustContentObjectToContainerSync();
      }
      if (marker.syncContentObjectPositionSync) {
        marker.syncContentObjectPositionSync();
      }
    });

    // НЕ вызываем requestRenderAll() здесь, чтобы избежать циклического рендеринга
  }

  updateMarkerPosition(): void {
    // 🎯 КРИТИЧНО: Пропускаем если объект в ActiveSelection
    if (this.group?.type.toLowerCase() === 'activeselection') {
      return;
    }

    // Ищем все маркеры связанные с этой сущностью (новый подход)
    let allMarkers = this.getAllLinkedMarkers();

    // Добавляем this.marker если он есть и не входит в allMarkers
    if (this.marker && !allMarkers.includes(this.marker)) {
      allMarkers = [...allMarkers, this.marker];
    }

    if (allMarkers.length === 0) {
      return; // Нет маркеров
    }

    const markerConfig = this.getMarkerConfig();
    if (!markerConfig) {
      return;
    }

    // 🎯 ИСПРАВЛЕНИЕ КООРДИНАТ: В ActiveSelection entity.left/top уже абсолютные!
    const entityLeft = this.left || 0;
    const entityTop = this.top || 0;
    let absoluteLeft = entityLeft;
    let absoluteTop = entityTop;

    if (this.group) {
      const isActiveSelection = this.group.type?.toLowerCase() === 'activeselection';

      // Для ActiveSelection НЕ складываем координаты!
      if (isActiveSelection) {
        absoluteLeft = entityLeft;
        absoluteTop = entityTop;
      } else {
        // Для других типов групп может быть нужно складывать
        absoluteLeft = (this.group.left || 0) + entityLeft;
        absoluteTop = (this.group.top || 0) + entityTop;
      }
    }

    // Обновляем старый персональный маркер (для обратной совместимости)
    if (this.marker) {
      this.marker.updatePosition(absoluteLeft, absoluteTop, this.height || 0, markerConfig);
    }

    // Обновляем все новые маркеры-контейнеры
    allMarkers.forEach((marker) => {
      // НОВАЯ КОНЦЕПЦИЯ: Маркер позиционируется СЛЕВА от текста с отступом (offsetX)
      const offsetX = markerConfig.position.offsetX || 0;
      const markerWidth = markerConfig.position.width;
      const markerHeight = markerConfig.position.height;

      // Рассчитываем координату ЛЕВОГО КРАЯ маркера
      const markerLeftEdge = absoluteLeft - markerWidth - offsetX;

      // Корректируем в зависимости от origin маркера
      const finalMarkerLeft =
        marker.originX === 'center'
          ? markerLeftEdge + markerWidth / 2 // Для center origin добавляем половину ширины
          : markerLeftEdge; // Для left origin оставляем как есть

      // Рассчитываем finalMarkerTop в зависимости от типа offsetY
      let markerTopEdge: number;
      const offsetY = markerConfig.position.offsetY;
      const entityHeight = this.height || 0;

      if (typeof offsetY === 'number') {
        markerTopEdge = absoluteTop + offsetY;
      } else if (offsetY === 'top') {
        markerTopEdge = absoluteTop;
      } else if (offsetY === 'center') {
        // Верхний край маркера для центрирования по вертикали
        markerTopEdge = absoluteTop + (entityHeight - markerHeight) / 2;
      } else if (offsetY === 'bottom') {
        // Верхняя граница маркера = нижняя граница текста минус высота маркера
        markerTopEdge = absoluteTop + entityHeight - markerHeight;
      } else if (offsetY === 'centered-by-first-line') {
        // Выравнивание по центру ПЕРВОЙ строки
        const fontSize = this.fontSize || 16;
        const lineHeight = this.lineHeight || 1.16;
        const firstLineHeight = fontSize * lineHeight;
        markerTopEdge = absoluteTop + (firstLineHeight - markerHeight) / 2;
      } else {
        markerTopEdge = absoluteTop;
      }

      // Корректируем в зависимости от originY маркера
      const finalMarkerTop =
        marker.originY === 'center'
          ? markerTopEdge + markerHeight / 2 // Для center origin добавляем половину высоты
          : markerTopEdge; // Для top origin оставляем как есть

      // Устанавливаем позицию маркера напрямую
      marker.set({
        left: finalMarkerLeft,
        top: finalMarkerTop,
        width: markerWidth,
        height: markerHeight,
      });
      marker.setCoords();

      // Синхронно обновляем контент маркера
      if (marker.adjustContentObjectToContainerSync) {
        marker.adjustContentObjectToContainerSync();
      }
      if (marker.syncContentObjectPositionSync) {
        marker.syncContentObjectPositionSync();
      }
    });

    // Обновляем канвас если он есть
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  }

  /**
   * Копирует все маркеры и их контент для новой TextEntity
   * @param newEntityId - ID новой сущности, для которой копируем маркеры
   * @param canvasId - ID canvas для генерации уникальных ID
   * @param skipAutoRecalculation - Пропустить автоматическую рекалькуляцию (будет вызвана вручную позже)
   */
  public copyMarkersToNewEntity(
    newEntityId: string,
    canvasId?: string,
    skipAutoRecalculation = false,
  ): void {
    if (!this.canvas) {
      console.warn('⚠️ Невозможно скопировать маркеры: отсутствует canvas');
      return;
    }

    // Получаем все маркеры связанные с текущей сущностью
    const existingMarkers = this.getAllLinkedMarkers();

    if (existingMarkers.length === 0) {
      return;
    }

    existingMarkers.forEach((originalMarker) => {
      // Генерируем новые ID
      const newMarkerId = canvasId
        ? generateObjectId(canvasId)
        : `marker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Находим новую сущность на канвасе для получения её позиции
      const newEntity = this.canvas!.getObjects().find(
        (obj) => obj.id === newEntityId,
      ) as TextEntity;

      if (!newEntity) {
        console.warn('⚠️ Новая сущность не найдена на канвасе:', newEntityId);
        return;
      }

      // Вычисляем новые координаты маркера относительно новой сущности
      // Получаем относительное смещение от исходной сущности
      const offsetX = (originalMarker.left || 0) - (this.left || 0);
      const offsetY = (originalMarker.top || 0) - (this.top || 0);

      // Применяем то же смещение к новой сущности
      const newMarkerLeft = (newEntity.left || 0) + offsetX;
      const newMarkerTop = (newEntity.top || 0) + offsetY;

      // Создаем новый маркер с правильными координатами
      const newMarker = new Marker({
        id: newMarkerId,
        entityId: newEntityId, // Привязываем к новой сущности
        entityType: originalMarker.entityType, // Тот же тип
        type: 'text', // Базовый тип по умолчанию
        content: '', // Пустой контент, заполнится после клонирования
        left: newMarkerLeft, // Новые координаты!
        top: newMarkerTop, // Новые координаты!
        width: originalMarker.width,
        height: originalMarker.height,
      });

      //  Устанавливаем тот же слой что у оригинального маркера
      newMarker.layer = originalMarker.layer;
      newMarker.zIndex = originalMarker.zIndex || 0;

      // ✅ Устанавливаем excludeFromBackup для маркера (чтобы не создавать бекап при добавлении)
      // Флаг будет сброшен после завершения всех операций
      newMarker.excludeFromBackup = true;

      // Добавляем новый маркер на канвас
      this.canvas!.add(newMarker);

      //  Добавляем маркер в систему слоёв для корректной работы Up/Down Layer
      try {
        const canvasStore = useCanvasStore.getState();
        const activeCanvasId = canvasStore.activeCanvasId;

        if (activeCanvasId) {
          canvasStore.addLayerObject(activeCanvasId, newMarker.layer, {
            object: newMarker,
            zIndex: newMarker.zIndex || 0,
            layer: newMarker.layer,
          });
        }
      } catch (error) {
        console.warn('⚠️ Не удалось добавить скопированный маркер в layersMap:', error);
      }

      // Устанавливаем обратную связь entity -> marker
      newEntity.marker = newMarker;
      newEntity.markerId = newMarkerId;

      // Ищем ВСЕ объекты контента оригинального маркера
      const originalContents = originalMarker.getContentObjects();
      if (originalContents.length > 0) {
        // Клонируем каждый объект контента
        const clonePromises = originalContents.map(async (originalContent) => {
          // Создаем копию контента
          const newContentId = canvasId
            ? generateObjectId(canvasId)
            : `content-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

          try {
            // Клонируем объект контента
            const clonedContent = await originalContent.clone();

            // Получаем сохраненные оффсеты оригинального контента относительно оригинального маркера
            // ВАЖНО: Оффсеты сохранены в ЦЕНТРАЛЬНОЙ системе координат
            const originalOffsetX = (originalContent as any)._markerOffsetX ?? 0;
            // ВАЖНО: offsetY всегда 0 - контент всегда на одной вертикали с центром маркера
            const originalOffsetY = 0;

            // Вычисляем ЦЕНТР нового маркера (учитываем что newMarker создается с left/top по умолчанию)
            const newMarkerOriginX = (newMarker as any).originX || 'left';
            const newMarkerOriginY = (newMarker as any).originY || 'top';
            const newMarkerWidth = newMarker.width || 16;
            const newMarkerHeight = newMarker.height || 16;

            let newMarkerCenterX = newMarkerLeft;
            let newMarkerCenterY = newMarkerTop;

            if (newMarkerOriginX === 'left') {
              newMarkerCenterX = newMarkerLeft + newMarkerWidth / 2;
            } else if (newMarkerOriginX === 'right') {
              newMarkerCenterX = newMarkerLeft - newMarkerWidth / 2;
            }

            if (newMarkerOriginY === 'top') {
              newMarkerCenterY = newMarkerTop + newMarkerHeight / 2;
            } else if (newMarkerOriginY === 'bottom') {
              newMarkerCenterY = newMarkerTop - newMarkerHeight / 2;
            }

            // Применяем оффсеты в центральной системе
            const newContentCenterX = newMarkerCenterX + originalOffsetX;
            const newContentCenterY = newMarkerCenterY + originalOffsetY;

            // Конвертируем в систему координат контента
            const contentOriginX = (clonedContent as any).originX || 'left';
            const contentOriginY = (clonedContent as any).originY || 'top';
            const contentWidth = (clonedContent.width || 0) * (clonedContent.scaleX || 1);
            const contentHeight = (clonedContent.height || 0) * (clonedContent.scaleY || 1);

            let newContentLeft = newContentCenterX;
            let newContentTop = newContentCenterY;

            if (contentOriginX === 'left') {
              newContentLeft = newContentCenterX - contentWidth / 2;
            } else if (contentOriginX === 'right') {
              newContentLeft = newContentCenterX + contentWidth / 2;
            }

            if (contentOriginY === 'top') {
              newContentTop = newContentCenterY - contentHeight / 2;
            } else if (contentOriginY === 'bottom') {
              newContentTop = newContentCenterY + contentHeight / 2;
            }

            // Обновляем ID, связь с маркером и позицию
            clonedContent.set({
              id: newContentId,
              markerId: newMarkerId, // Привязываем к новому маркеру
              left: newContentLeft, // Позиция с учетом оффсетов и конвертации координат
              top: newContentTop, // Позиция с учетом оффсетов и конвертации координат
            });

            //  Устанавливаем тот же слой что у оригинального контента
            clonedContent.layer = originalContent.layer || originalMarker.layer;
            clonedContent.zIndex = originalContent.zIndex || originalMarker.zIndex || 0;

            // 🎯 АВТОМАТИЧЕСКОЕ масштабирование для картинок-контента маркеров
            // Масштабируем если флаг не установлен явно в false
            if (clonedContent.type === 'image' && (clonedContent as any)._originalElement) {
              const scaleFlag = (clonedContent as any).isNeedScaleImgForFirstRender;
              const isScaleFlagExplicitlyFalse = scaleFlag === false;

              // Масштабируем если флаг НЕ установлен явно в false
              const shouldScale = !isScaleFlagExplicitlyFalse;

              if (shouldScale) {
                const targetWidth = clonedContent.width! * clonedContent.scaleX!;
                const targetHeight = clonedContent.height! * clonedContent.scaleY!;

                // Масштабируем картинку к нужным размерам
                await scaleImage(clonedContent as any, targetWidth, targetHeight);
              }
            }

            // Добавляем клонированный контент на канвас
            this.canvas!.add(clonedContent);

            //  Добавляем контент маркера в систему слоёв для корректной работы Up/Down Layer
            try {
              const canvasStore = useCanvasStore.getState();
              const activeCanvasId = canvasStore.activeCanvasId;

              if (activeCanvasId) {
                canvasStore.addLayerObject(activeCanvasId, clonedContent.layer, {
                  object: clonedContent,
                  zIndex: clonedContent.zIndex || 0,
                  layer: clonedContent.layer,
                });
              }
            } catch (error) {
              console.warn(
                '⚠️ Не удалось добавить скопированный контент маркера в layersMap:',
                error,
              );
            }

            // ✅ Устанавливаем excludeFromBackup для контента (чтобы не создавать бекап при добавлении)
            // Флаг будет сброшен после завершения всех операций
            clonedContent.excludeFromBackup = true;

            // Добавляем клонированный контент к новому маркеру
            newMarker.addContentObject(clonedContent);
          } catch (error) {
            console.error('❌ Ошибка при клонировании объекта контента маркера:', {
              originalContentId: originalContent.id,
              error,
            });
          }
        });

        // Ждем завершения клонирования всех объектов контента
        Promise.all(clonePromises).then(() => {
          // Обновляем рендер после добавления всех объектов
          this.canvas!.requestRenderAll();
        });
      }
    });

    // После копирования всех маркеров - пересчитываем позиции MetaBullet
    setTimeout(() => {
      if (skipAutoRecalculation) {
        return;
      }

      const system = (this.canvas as any)._metaBulletSystem;
      if (system) {
        const metaBullet = system.getMetaBullet(this.metaBulletId);
        if (metaBullet) {
          // Сначала обновляем позиции всех маркеров в MetaBullet
          const entities = metaBullet.getEntities();
          entities.forEach((entity: TextEntity) => {
            if (entity.updateMarkerPosition) {
              entity.updateMarkerPosition();
            }
          });

          // Затем пересчитываем позиции с учетом высоты маркеров
          metaBullet.recalculatePositions(false); // Без анимации для быстроты

          // Проверяем связь между новой сущностью и её маркерами
          const newEntityObj = this.canvas!.getObjects().find(
            (obj) => obj.id === newEntityId,
          ) as TextEntity;
          if (newEntityObj) {
            // Принудительный тест связи - пробуем вызвать updateMarkerPosition у новой сущности
            if (newEntityObj && typeof newEntityObj.updateMarkerPosition === 'function') {
              newEntityObj.updateMarkerPosition();
            }
          }
        }
      }
    }, 100); // Даем время завершиться всем clone().then() операциям
  }

  /**
   * Находит все маркеры связанные с этой TextEntity на канвасе
   */
  private getAllLinkedMarkers(): Marker[] {
    if (!this.canvas) {
      return [];
    }

    const allObjects = this.canvas.getObjects();
    const foundMarkers = allObjects.filter(
      (obj): obj is Marker => obj.type === 'marker' && !!obj.isMarker && obj.entityId === this.id,
    ) as Marker[];

    return foundMarkers;
  }

  /**
   * Получает конфигурацию маркера для текущего типа сущности
   * Объединяет настройки из стилей с персональными настройками сущности
   */
  private getMarkerConfig(): MarkerLayoutConfig | undefined {
    const entityStyle = this.stylesConfig[this.entityType];

    // Digital-accent не поддерживает маркеры
    if (this.isDigitalAccent()) {
      return undefined;
    }

    const markerLayout = 'marker' in entityStyle ? entityStyle.marker : undefined;

    if (!markerLayout || !markerLayout.enabled) {
      return undefined;
    }

    // В новой концепции контент передается с бэкенда отдельно
    // Возвращаем только конфигурацию позиции и стилей
    return {
      enabled: markerLayout.enabled,
      position: markerLayout.position,
      style: markerLayout.style || {},
    };
  }

  /**
   * Обновляет маркер при изменении типа сущности
   */
  private updateMarkerForNewType(): void {
    const markerConfig = this.getMarkerConfig();

    if (markerConfig && markerConfig.enabled) {
      // Новый тип ТРЕБУЕТ маркер
      if (this.marker) {
        // Маркер уже есть - обновляем позицию

        this.updateMarkerPosition();
        this.canvas?.requestRenderAll();
      } else if (this.lastMarkerState) {
        // Маркера нет, но есть сохраненное состояние - восстанавливаем

        // Восстанавливаем данные маркера
        this.marker = this.lastMarkerState.marker;
        this.markerId = this.lastMarkerState.markerId;
        this.markerContent = this.lastMarkerState.markerContent;
        this.markerType = this.lastMarkerState.markerType;

        // Добавляем маркер обратно на canvas
        this.addMarkerToCanvas();

        // Очищаем сохраненное состояние после восстановления
        this.lastMarkerState = undefined;
      }
    } else if (this.marker) {
      // Новый тип НЕ требует маркер, но он есть - сохраняем и УДАЛЯЕМ с canvas

      // Сохраняем состояние маркера для возможности восстановления
      this.lastMarkerState = {
        markerId: this.markerId!,
        marker: this.marker,
        markerContent: this.markerContent,
        markerType: this.markerType,
      };

      // УДАЛЯЕМ маркер с canvas (не скрываем!)
      this.removeMarkerFromCanvas();

      // Обнуляем ссылки после удаления
      this.marker = undefined;
      this.markerId = undefined;
    }
  }

  /**
   * Получить маркер связанный с этой сущностью
   */
  getMarker(): Marker | undefined {
    return this.marker;
  }

  /**
   * Проверить есть ли у сущности маркер
   */
  hasMarker(): boolean {
    return !!this.marker;
  }

  /**
   * Изменить маркер (для будущего функционала смены маркеров)
   */
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  changeMarker(newContent: string, newType?: MarkerType): void {
    if (!this.marker) {
      console.warn('⚠️ Попытка изменить маркер у сущности без маркера:', this.id);
      return;
    }

    // updateContent устарел в новой концепции маркеров
    // Контент теперь управляется через отдельные объекты с бэкенда

    // Обновляем канвас
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  }

  // === МЕТОДЫ ДЛЯ DIGITAL-ACCENT ===

  /**
   * Получить связанную сущность digital-accent (digit <-> superscript)
   */
  getLinkedAccentEntity(): TextEntity | null {
    if (!this.linkedAccentEntityId || !this.canvas) {
      return null;
    }

    // Используем кэш если есть
    if (this.linkedAccentEntity?.id === this.linkedAccentEntityId) {
      return this.linkedAccentEntity;
    }

    // Ищем на canvas
    const linkedObj = this.canvas.getObjects().find((obj) => obj.id === this.linkedAccentEntityId);
    if (linkedObj && TextEntity.isTextEntity(linkedObj)) {
      this.linkedAccentEntity = linkedObj;
      return linkedObj;
    }

    return null;
  }

  /**
   * Установить связанную сущность digital-accent
   */
  setLinkedAccentEntity(linkedEntity: TextEntity): void {
    this.linkedAccentEntityId = linkedEntity.id;
    this.linkedAccentEntity = linkedEntity;

    // Устанавливаем обратную связь
    linkedEntity.linkedAccentEntityId = this.id;
    linkedEntity.linkedAccentEntity = this;
  }

  /**
   * Проверить является ли эта сущность частью digital-accent
   */
  isDigitalAccent(): boolean {
    return isDigitalAccentType(this.entityType);
  }

  /**
   * Проверить является ли эта сущность digit в digital-accent
   */
  isDigitalAccentDigit(): boolean {
    return isDigitalAccentDigit(this.entityType);
  }

  /**
   * Проверить является ли эта сущность superscript в digital-accent
   */
  isDigitalAccentSuperscript(): boolean {
    return isDigitalAccentSuperscript(this.entityType);
  }

  /**
   * Синхронизировать позицию связанной сущности (для digit -> superscript)
   */
  updateLinkedAccentPosition(): void {
    if (!this.isDigitalAccentDigit()) {
      return; // Только digit управляет позицией superscript
    }

    const linkedEntity = this.getLinkedAccentEntity();
    if (!linkedEntity || !linkedEntity.isDigitalAccentSuperscript()) {
      return;
    }

    const config = this.getDigitalAccentConfig();
    if (!config) {
      return;
    }

    // Рассчитываем позицию superscript относительно digit
    const digitWidth = this.width || 0;
    const digitHeight = this.height || 0;
    const digitLeft = this.left || 0;
    const digitTop = this.top || 0;

    const superscriptLeft = digitLeft + digitWidth + config.superscript.offsetX;
    const superscriptHeight = linkedEntity.height || 0;
    const offsetY = config.superscript.offsetY;

    let superscriptTop: number;

    if (typeof offsetY === 'number') {
      superscriptTop = digitTop + offsetY;
    } else if (offsetY === 'top') {
      superscriptTop = digitTop;
    } else if (offsetY === 'center') {
      superscriptTop = digitTop + (digitHeight - superscriptHeight) / 2;
    } else if (offsetY === 'bottom') {
      superscriptTop = digitTop + digitHeight - superscriptHeight;
    } else if (offsetY === 'centered-by-first-line') {
      const fontSize = this.fontSize || 16;
      const lineHeight = this.lineHeight || 1.16;
      const firstLineHeight = fontSize * lineHeight;
      superscriptTop = digitTop + (firstLineHeight - superscriptHeight) / 2;
    } else {
      superscriptTop = digitTop;
    }

    // Обновляем позицию superscript
    linkedEntity.set({
      left: superscriptLeft,
      top: superscriptTop,
    });
    linkedEntity.setCoords();

    // Обновляем canvas
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  }

  /**
   * Получить конфигурацию digital-accent из стилей
   */
  getDigitalAccentConfig() {
    // Сначала пробуем получить из локального конфига
    const stylesConfig = this.getStylesConfig();
    let digitalAccentConfig = stylesConfig?.['digital-accent'];

    // Если не найден, получаем из глобального конфига canvas store
    if (!digitalAccentConfig) {
      try {
        const canvasStore = useCanvasStore.getState();
        // Пробуем разные места в store
        const globalConfig =
          (canvasStore as any).textEntityStyles ||
          (canvasStore as any).config?.textEntityStyles ||
          (canvasStore as any).canvasConfig?.textEntityStyles;

        if (globalConfig?.['digital-accent']) {
          digitalAccentConfig = globalConfig['digital-accent'];
        }
      } catch (error) {
        console.error('🔢 Could not access canvas store:', error);
      }
    }

    // Если все еще не найден, используем дефолтный конфиг из mergeStylesConfig
    if (!digitalAccentConfig) {
      try {
        const defaultConfig = mergeStylesConfig();
        digitalAccentConfig = defaultConfig['digital-accent'];
      } catch (error) {
        console.error('🔢 Could not get default config:', error);
      }
    }

    if (
      !digitalAccentConfig ||
      typeof digitalAccentConfig !== 'object' ||
      !('digit' in digitalAccentConfig)
    ) {
      console.warn('⚠️ Digital-accent config not found in any source');
      return null;
    }

    // Применяем персональные стили если они есть
    const finalConfig = { ...digitalAccentConfig };

    if (this.personalDigitStyles) {
      finalConfig.digit = {
        ...finalConfig.digit,
        ...this.personalDigitStyles,
      };
    }

    if (this.personalSuperscriptStyles) {
      finalConfig.superscript = {
        ...finalConfig.superscript,
        ...this.personalSuperscriptStyles,
      };
    }

    return finalConfig;
  }

  /**
   * Создать новый параграф после группы digital-accent
   */
  private _createParagraphAfterDigitalAccent(): void {
    // Находим digit сущность (главную в группе)
    const digitEntity = this.isDigitalAccentDigit() ? this : this.getLinkedAccentEntity();

    if (!digitEntity?.isDigitalAccentDigit()) {
      console.warn('⚠️ Cannot find digit entity in digital-accent group');
      return;
    }

    // Создаем новый параграф после digit entity
    const canvas = this.canvas;
    if (!canvas?._metaBulletSystem || !digitEntity) {
      console.warn('⚠️ MetaBulletSystem not found or digitEntity is null');
      return;
    }

    // Создаем новую сущность в том же метабуллите после digit
    const newEntity = canvas._metaBulletSystem.createEntityInMetaBullet(
      this.metaBulletId,
      digitEntity.id, // После digit entity
      '', // Пустой текст
      'bullet-level-1', // Тип базового буллита (уровень 1)
    );

    // Переходим в режим редактирования новой сущности
    if (newEntity) {
      this.exitEditing();
      setTimeout(() => {
        newEntity.enterEditing();
      }, 50);
    }
  }

  /**
   * Обработка Backspace для digital-accent (когда сущность пустая)
   */
  private _handleDigitalAccentBackspace(): void {
    if (this.isDigitalAccentDigit()) {
      // Если это digit и он пустой - удаляем его вместе с superscript и переключаемся на предыдущую сущность
      this._deleteEmptyEntityAndFocusPrevious();
    } else if (this.isDigitalAccentSuperscript()) {
      // Если это superscript и он пустой - переключаемся на digit
      const linkedDigitEntity = this.getLinkedAccentEntity();
      if (linkedDigitEntity) {
        this.exitEditing();
        setTimeout(() => {
          linkedDigitEntity.enterEditing();
          // Ставим курсор в конец digit
          linkedDigitEntity.selectAll();
          linkedDigitEntity.setSelectionEnd(linkedDigitEntity.text?.length || 0);
        }, 50);
      } else {
        // Если digit не найден - переключаемся на предыдущую сущность
        this._focusPreviousEntityOnly();
      }
    }
  }

  /**
   * Переключение на предыдущую сущность для digital-accent
   */
  private _focusPreviousEntityDigitalAccent(): void {
    if (this.isDigitalAccentSuperscript()) {
      // Если мы в superscript - переключаемся на digit
      const linkedDigitEntity = this.getLinkedAccentEntity();
      if (linkedDigitEntity) {
        this.exitEditing();
        setTimeout(() => {
          linkedDigitEntity.enterEditing();
          // Ставим курсор в конец digit
          linkedDigitEntity.selectAll();
          linkedDigitEntity.setSelectionEnd(linkedDigitEntity.text?.length || 0);
        }, 50);
        return;
      }
    }

    // Иначе переключаемся на предыдущую сущность в метабуллите
    this._focusPreviousEntityOnly();
  }

  // Статические свойства для автоматической сериализации кастомных полей
  static customProperties = [
    'entityType',
    'metaBulletId',
    'entityOrder',
    'bulletLevel',
    'isMetaBulletEntity',
    'marginBottom',
    'marginLeft',
    'excludeFromBackup',
    'markerId', // ID связанного маркера
    'markerContent', // Персональное содержимое маркера
    'markerType', // Персональный тип маркера
    'stylesConfig', // Конфигурация стилей для сериализации
    // Digital-accent свойства
    'accentGroupId', // ID группы для связывания digit и степени
    'accentRole', // Роль в группе digital-accent
    'linkedAccentEntityId', // ID связанной сущности
    'personalDigitStyles', // Персональные стили для digit
    'personalSuperscriptStyles', // Персональные стили для superscript
  ];
}
